import { useState, useCallback, useEffect, useRef } from "react";

// Drives every mobile layout decision across the dashboard — bottom nav vs
// sidebar, stacked vs side-by-side grids, the calendar's day-pager, etc.
// Checks BOTH viewport width and touch capability, not width alone — a
// setting like Safari's "Request Desktop Website" (Settings > Safari >
// Request Desktop Website > All Websites) makes a real phone report a
// virtual desktop-sized viewport, which a width-only check would wrongly
// read as "desktop." Requiring touch support too catches that case: an
// actual desktop with a mouse won't have coarse pointer/touch support, so
// this doesn't accidentally trigger the mobile layout on a real desktop.
export function useIsMobile() {
  const evaluate = () => {
    if (typeof window === "undefined") return false;
    const narrow = window.matchMedia("(max-width: 768px)").matches;
    const isTouchDevice = navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches;
    // Also true for anything narrow-ish (under 900px) AND touch-driven, so a
    // phone stuck rendering at a forced desktop width still gets caught.
    const narrowish = window.matchMedia("(max-width: 900px)").matches;
    return narrow || (narrowish && isTouchDevice);
  };
  const [isMobile, setIsMobile] = useState(evaluate);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const mq2 = window.matchMedia("(max-width: 900px)");
    const onChange = () => setIsMobile(evaluate());
    mq.addEventListener("change", onChange);
    mq2.addEventListener("change", onChange);
    return () => { mq.removeEventListener("change", onChange); mq2.removeEventListener("change", onChange); };
  }, []);
  return isMobile;
}


// ─── THEME TOKENS ───────────────────────────────────────────────────────────
// Light is the :root default (also the prefers-color-scheme:light case);
// dark is layered on via both a prefers-color-scheme media query (so a
// first-time visitor gets their OS preference) and a [data-theme="dark"]
// attribute override (so the in-app toggle — see ThemeToggle below — always
// wins over the OS setting in either direction). Every color in the app
// should reference one of these custom properties, never a literal hex, so
// the toggle actually reaches everything.
//
// Accent hues (--blue, --cyan, etc.) intentionally do NOT get redefined per
// theme — they read fine as fills/borders/tinted badges on both light and
// dark panels. The "-text" variants exist for the handful of places an
// accent is used as literal text color, where the raw hue can wash out on
// white; each is derived from the hue via color-mix() toward the theme's
// own --ink, so light mode automatically gets a deepened, readable variant
// while dark mode's is defined to equal the raw hue — pixel-identical to
// what this app already looked like before light mode existed.
export const THEME_TOKENS = `
  :root {
    --blue: #4f8ef7; --cyan: #38bdf8; --red: #f87171; --green: #34d399;
    --amber: #fbbf24; --purple: #a78bfa; --orange: #fb923c;
    /* Text color for solid accent-filled buttons (Save/Delete/Send/etc) —
       contrasts against the FILL, which is constant across themes, so this
       stays constant too rather than tracking --bg (which flips to white
       in light mode and would make solid-button text unreadable). */
    --on-accent: #14171f;

    --bg: #f4f5fa;
    --panel: #ffffff;
    --panel-soft: #f8f9fd;
    --panel-alt: #eef0f7;
    --border: #e2e5ef;
    --border-soft: #dde1ec;
    --ink: #181b24;
    --ink-soft: #6b7280;
    --ink-faint: #9aa0b0;
    --ink-mid: #4b5468;
    --ink-dim: #9199ab;
    --ink-disabled: #c3c8d4;
    --overlay: rgba(20,22,30,0.35);

    --cal-today-row: #eef2fb;
    --cal-odd-row: #fbfbfd;
    --cal-gridline: #e8eaf1;

    --danger-soft-border: #f5c6c6;
    --success-soft-bg: #e8f9f1;
    --success-soft-border: #c8ecda;
    --info-soft-bg: #e8f0fd;

    --blue-text: color-mix(in srgb, var(--blue) 72%, var(--ink) 28%);
    --cyan-text: color-mix(in srgb, var(--cyan) 72%, var(--ink) 28%);
    --red-text: color-mix(in srgb, var(--red) 72%, var(--ink) 28%);
    --green-text: color-mix(in srgb, var(--green) 72%, var(--ink) 28%);
    --amber-text: color-mix(in srgb, var(--amber) 72%, var(--ink) 28%);
    --purple-text: color-mix(in srgb, var(--purple) 72%, var(--ink) 28%);
    --orange-text: color-mix(in srgb, var(--orange) 72%, var(--ink) 28%);
    --danger-badge-text: #a83232;
    --warning-badge-text: #8a5f0a;
  }
  @media (prefers-color-scheme: dark) {
    :root {
      --bg: #0f1117; --panel: #131620; --panel-soft: #151820; --panel-alt: #1f2333;
      --border: #2a2f42; --border-soft: #2e3347;
      --ink: #f0f2f8; --ink-soft: #a0a8bc; --ink-faint: #6a7080;
      --ink-mid: #b8c0d0; --ink-dim: #8890a0; --ink-disabled: #4a5065;
      --overlay: rgba(0,0,0,0.63);
      --cal-today-row: #141720; --cal-odd-row: #111318; --cal-gridline: #1a1d27;
      --danger-soft-border: #3b1a1a; --success-soft-bg: #0d1f1a; --success-soft-border: #1a3a2a; --info-soft-bg: #0d1a2e;
      --blue-text: var(--blue); --cyan-text: var(--cyan); --red-text: var(--red); --green-text: var(--green);
      --amber-text: var(--amber); --purple-text: var(--purple); --orange-text: var(--orange);
      --danger-badge-text: #ff9999; --warning-badge-text: #fde68a;
    }
  }
  :root[data-theme="dark"] {
    --bg: #0f1117; --panel: #131620; --panel-soft: #151820; --panel-alt: #1f2333;
    --border: #2a2f42; --border-soft: #2e3347;
    --ink: #f0f2f8; --ink-soft: #a0a8bc; --ink-faint: #6a7080;
    --ink-mid: #b8c0d0; --ink-dim: #8890a0; --ink-disabled: #4a5065;
    --overlay: rgba(0,0,0,0.63);
    --cal-today-row: #141720; --cal-odd-row: #111318; --cal-gridline: #1a1d27;
    --danger-soft-border: #3b1a1a; --success-soft-bg: #0d1f1a; --success-soft-border: #1a3a2a; --info-soft-bg: #0d1a2e;
    --blue-text: var(--blue); --cyan-text: var(--cyan); --red-text: var(--red); --green-text: var(--green);
    --amber-text: var(--amber); --purple-text: var(--purple); --orange-text: var(--orange);
    --danger-badge-text: #ff9999; --warning-badge-text: #fde68a;
  }
  :root[data-theme="light"] {
    --bg: #f4f5fa; --panel: #ffffff; --panel-soft: #f8f9fd; --panel-alt: #eef0f7;
    --border: #e2e5ef; --border-soft: #dde1ec;
    --ink: #181b24; --ink-soft: #6b7280; --ink-faint: #9aa0b0;
    --ink-mid: #4b5468; --ink-dim: #9199ab; --ink-disabled: #c3c8d4;
    --overlay: rgba(20,22,30,0.35);
    --cal-today-row: #eef2fb; --cal-odd-row: #fbfbfd; --cal-gridline: #e8eaf1;
    --danger-soft-border: #f5c6c6; --success-soft-bg: #e8f9f1; --success-soft-border: #c8ecda; --info-soft-bg: #e8f0fd;
    --blue-text: color-mix(in srgb, var(--blue) 72%, var(--ink) 28%);
    --cyan-text: color-mix(in srgb, var(--cyan) 72%, var(--ink) 28%);
    --red-text: color-mix(in srgb, var(--red) 72%, var(--ink) 28%);
    --green-text: color-mix(in srgb, var(--green) 72%, var(--ink) 28%);
    --amber-text: color-mix(in srgb, var(--amber) 72%, var(--ink) 28%);
    --purple-text: color-mix(in srgb, var(--purple) 72%, var(--ink) 28%);
    --orange-text: color-mix(in srgb, var(--orange) 72%, var(--ink) 28%);
    --danger-badge-text: #a83232; --warning-badge-text: #8a5f0a;
  }
`;

export function loadTheme() { return loadLocal("theme", null) || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"); }
export function applyTheme(theme) { document.documentElement.setAttribute("data-theme", theme); saveLocal("theme", theme); }

export function ThemeToggle({ theme, onChange }) {
  const isDark = theme === "dark";
  return (
    <button
      onClick={() => onChange(isDark ? "light" : "dark")}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px 5px 5px", borderRadius:999, border:"1px solid var(--border)", background:"var(--panel-alt)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}
    >
      <span style={{ width:20, height:20, borderRadius:"50%", background:"var(--ink)", color:"var(--bg)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11 }}>{isDark ? "☾" : "☀"}</span>
      <span style={{ fontSize:11, fontWeight:600, color:"var(--ink-soft)" }}>{isDark ? "Dark" : "Light"}</span>
    </button>
  );
}

export const FONT_INJECT = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=Playfair+Display:wght@600;700&family=DM+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--ink); transition: background 0.15s ease, color 0.15s ease; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: var(--cal-gridline); }
  ::-webkit-scrollbar-thumb { background: var(--border-soft); border-radius: 3px; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-6px); } to { opacity:1; transform:translateX(0); } }
  @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
  .fade-up { animation: fadeUp 0.35s ease both; }
  .slide-in { animation: slideIn 0.25s ease both; }
  .pulse-dot { animation: pulse 2s ease infinite; }
  .spin { animation: spin 1s linear infinite; }
  button:hover { opacity: 0.85; }
`;

export const LENSES = [
  { id: "all",    label: "All",    sub: "Unified Command",     accent: "var(--blue)",   accentText: "var(--blue-text)" },
  { id: "work",   label: "Work",   sub: "Crowe & Clients",     accent: "var(--cyan)",   accentText: "var(--cyan-text)" },
  { id: "home",   label: "Home",   sub: "Family & Brenna",     accent: "var(--red)",    accentText: "var(--red-text)" },
  { id: "gmu",    label: "GMU",    sub: "Teaching & Students", accent: "var(--green)",  accentText: "var(--green-text)" },
  { id: "growth", label: "Growth", sub: "Wellness & Learning", accent: "var(--amber)",  accentText: "var(--amber-text)" },
];

// `lensAccent` is for anything NOT literal text — backgrounds, borders,
// color-mix() tint sources. `lensAccentText` is for literal text color,
// where the raw hue can wash out on a light panel; it resolves to a
// deepened variant on light and the identical hue on dark (see THEME_TOKENS).
export function lensAccent(id) { return LENSES.find(l => l.id === id)?.accent ?? "var(--blue)"; }
export function lensAccentText(id) { return LENSES.find(l => l.id === id)?.accentText ?? "var(--blue-text)"; }
export function lensLabel(id)  { return LENSES.find(l => l.id === id)?.label ?? id; }

// Replaces the old `color+"22"`-style hex-alpha-suffix trick, which breaks
// once `color` is a CSS var() reference instead of a raw hex string. `pct`
// is 0-100.
export function tint(color, pct) { return `color-mix(in srgb, ${color} ${pct}%, transparent)`; }
export function byLens(arr, lens) { return lens === "all" ? arr : arr.filter(i => i.lens === lens); }

export function isToday(dateStr) {
  if (!dateStr) return false;
  const today = new Date();
  const d = new Date(dateStr + "T12:00:00");
  return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
}

export function isOverdue(dateStr) {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr + "T12:00:00") < today;
}

export function dueDayLabel(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function toDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
}

function getWeekDates(weekOffset = 0) {
  const today = new Date();
  const sun = new Date(today);
  sun.setDate(today.getDate() - today.getDay() + weekOffset * 7);
  sun.setHours(0,0,0,0);
  return Array.from({length:7},(_,i) => { const d = new Date(sun); d.setDate(sun.getDate()+i); return d; });
}

function trunc(str, max) {
  if (!str) return "";
  return str.length > max ? str.slice(0,max)+"…" : str;
}

export function saveLocal(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }
export function loadLocal(key, fallback) {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : fallback; } catch { return fallback; }
}

export const panel = { background:"var(--panel)", border:"1px solid var(--border)", borderRadius:12, padding:"18px 16px" };
export const monoLabel = { fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"'DM Mono',monospace", marginBottom:12, color:"var(--ink-soft)" };

// ─── LENS TAB ─────────────────────────────────────────────────────────────────
export function LensTab({ lens, active, onClick }) {
  return (
    <button onClick={onClick} style={{ padding:"9px 16px", background:"transparent", color:active?lens.accentText:"var(--ink-soft)", cursor:"pointer", fontSize:13, fontWeight:active?600:400, fontFamily:"'DM Sans',sans-serif", transition:"all 0.15s", whiteSpace:"nowrap", border:"none", borderBottom:active?`2px solid ${lens.accent}`:"2px solid transparent" }}>
      {lens.label}
    </button>
  );
}

// ─── KPI STRIP ────────────────────────────────────────────────────────────────
export function KpiStrip({ lens, events, tasks, goals }) {
  const isMobile = useIsMobile();
  const accent = lensAccent(lens);
  const ft = byLens(tasks, lens);
  const todayKey = toDateKey(new Date());
  const todayCount = (events||[]).filter(e => e.dateKey === todayKey).length;
  const openTasks = ft.filter(t => t.col !== "Done").length;
  const activeGoals = byLens(goals,lens).filter(g => !g.complete).length;
  const overdueTasks = ft.filter(t => t.col !== "Done" && isOverdue(t.dueDate)).length;
  const kpis = [
    { label:"Open Tasks",   value:String(openTasks),    sub:`${ft.filter(t=>t.col==="In Progress").length} in progress` },
    { label:"Events Today", value:String(todayCount),   sub:"from your calendar" },
    { label:"Active Goals", value:String(activeGoals),  sub:"in progress" },
    { label:"Overdue",      value:String(overdueTasks), sub:"need attention", alert:overdueTasks>0 },
  ];
  return (
    <div style={{ display:"grid", gridTemplateColumns:`repeat(${isMobile ? 2 : 4},1fr)`, gap:10 }}>
      {kpis.map(k => (
        <div key={k.label} style={{ ...panel, borderLeft:`3px solid ${k.alert&&k.value!=="0"?"var(--red)":accent}`, padding:"12px 14px" }}>
          <div style={{ ...monoLabel, marginBottom:6, color:k.alert&&k.value!=="0"?"var(--red-text)":"var(--ink-soft)" }}>{k.label}</div>
          <div style={{ fontSize:26, fontWeight:600, color:k.alert&&k.value!=="0"?"var(--danger-badge-text)":"var(--ink)", fontFamily:"'Playfair Display',serif", lineHeight:1 }}>{k.value}</div>
          <div style={{ fontSize:11, color:"var(--ink-soft)", marginTop:4 }}>{k.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ─── AI BRIEFING ──────────────────────────────────────────────────────────────
// Cached under one shared key — valid only for today's date AND the lens it
// was generated for, so a stale/mismatched-lens cache from an earlier day or
// a manual refresh on a different lens never gets shown as if it were fresh.
function validBriefingCache(lens) {
  const cached = loadLocal("ai-briefing", null);
  return cached && cached.date === toDateKey(new Date()) && cached.lens === lens ? cached : null;
}

export function AiBriefing({ lens, events, tasks, goals, autoRun }) {
  const [text, setText] = useState(() => validBriefingCache(lens)?.text || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasAutoRun = useRef(false);
  const lMeta = LENSES.find(l => l.id === lens);
  const accent = lMeta?.accent ?? "var(--blue)";
  const accentText = lMeta?.accentText ?? "var(--blue-text)";

  const generate = useCallback(async () => {
    setLoading(true); setText(""); setError(null);
    const todayKey = toDateKey(new Date());
    const evList = (events||[]).filter(e=>e.dateKey===todayKey).map(e=>`${e.time||"all day"} ${e.title}`).join("; ")||"None";
    const openTasks = byLens(tasks,lens).filter(t=>t.col!=="Done");
    const tkList = openTasks.map(t=>`[${t.priority||"Normal"}] ${t.title}${t.dueDate?` due ${t.dueDate}`:""}`).join("; ")||"None";
    // Due-or-overdue tasks that are also linked to a goal are the single
    // highest-priority thing the briefing can surface — flagged separately
    // so the model can't bury them behind an unrelated "urgent" task.
    const priorityList = openTasks.filter(t=>t.goalId && t.dueDate && t.dueDate<=todayKey).map(t=>`${t.title}${isOverdue(t.dueDate)?` (overdue since ${t.dueDate})`:" (due today)"}`).join("; ")||"None";
    // Goal lines now carry phase/linked-task detail when available, so the
    // briefing can say something specific ("2 of 3 phase-1 tasks done") in
    // its goals section rather than just a bare number.
    const glList = byLens(goals,lens).filter(g=>!g.complete).map(g=>{
      if (g.progressMode === "tasks") {
        const linked = (tasks||[]).filter(t=>t.goalId===g.id);
        const done = linked.filter(t=>t.col==="Done").length;
        const activePhase = g.phases?.find(p=>!p.complete);
        return `${g.title} (${done}/${linked.length} linked tasks done${activePhase?`, current phase: ${activePhase.title}`:""}${g.dueDate?`, due ${g.dueDate}`:""})`;
      }
      return `${g.title} (${g.progress}/${g.target}${g.unit?` ${g.unit}`:""}${g.dueDate?`, due ${g.dueDate}`:""})`;
    }).join("; ")||"None";
    const prompt = `You are the personal command center AI for Sean Prince — CPA/CFA, Crowe LLP National Office Partner (revenue recognition SME, leads ASC 606 team), GMU adjunct professor teaching Intermediate Accounting, father of 8 children aged 1-15, based in Northern Virginia.\n\nCurrent lens: "${lMeta?.label}" (${lMeta?.sub})\nToday: ${new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}\nCalendar events today: ${evList}\nOpen tasks: ${tkList}\nDue-or-overdue tasks linked to a goal (highest priority — these matter more than any other open task): ${priorityList}\nActive goals: ${glList}\n\nWrite Sean's morning briefing for the "${lMeta?.label}" lens. Be direct, precise, and peer-level for the calendar/tasks portion. If any due-or-overdue goal-linked tasks are listed above, open with those by name before anything else. Otherwise lead with the most critical item and flag anything overdue.\n\nFor the goals portion specifically, shift tone: be warm, energetic, and genuinely encouraging, like a coach in his corner, not an auditor. If a goal has concrete phase/task progress, reference it specifically rather than generically ("2 of 3 tasks done on phase 1" beats "keep it up"). If a goal looks stalled, say so plainly but kindly, and suggest one concrete next step.\n\n3-5 tight sentences total. End with one clear recommended first action. No bullet points. No preamble.`;
    try {
      const res = await fetch("https://anthropic-proxy-worker.seancprince.workers.dev", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:1000,messages:[{role:"user",content:prompt}]}) });
      const data = await res.json();
      const full = data.content?.find(b=>b.type==="text")?.text ?? "Unable to generate briefing.";
      let i=0; const iv=setInterval(()=>{i+=4;setText(full.slice(0,i));if(i>=full.length){clearInterval(iv);saveLocal("ai-briefing",{date:toDateKey(new Date()),lens,text:full});}},10);
    } catch { setError("AI briefing will activate once deployed with API key."); }
    finally { setLoading(false); }
  }, [lens, events, tasks, goals, lMeta]);

  // Auto-run once per app open — App.js flips `autoRun` true only after
  // tasks/goals have finished their initial sync fetch, so this never fires
  // against stale default data. Skips the API call entirely if today's
  // briefing for this lens is already cached.
  useEffect(() => {
    if (!autoRun || hasAutoRun.current) return;
    hasAutoRun.current = true;
    if (validBriefingCache(lens)) return;
    generate();
  }, [autoRun, lens, generate]);

  return (
    <div style={{ ...panel, borderColor:tint(accent, 27), borderLeft:`3px solid ${accent}` }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ ...monoLabel, color:accentText, marginBottom:0 }}>✦ AI Briefing</div>
        <button onClick={generate} disabled={loading} style={{ background:"transparent", border:"none", cursor:"pointer", color:"var(--ink-soft)", fontSize:11, fontFamily:"'DM Sans',sans-serif", display:"flex", alignItems:"center", gap:5 }}>
          <span className={loading?"spin":""} style={{display:"inline-block"}}>↺</span> {loading?"Thinking…":"Refresh"}
        </button>
      </div>
      <div style={{ fontSize:14, lineHeight:1.8, color:loading&&!text?"var(--ink-soft)":"var(--ink)", minHeight:56 }}>
        {loading&&!text ? <span><span className="pulse-dot" style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:accent,marginRight:8}}/>Analyzing your day…</span>
          : error ? <span style={{color:"var(--red-text)"}}>{error}</span> : text}
      </div>
    </div>
  );
}

// ─── NEWS FEED / CURATED INTELLIGENCE ──────────────────────────────────────────
// Each category gets its own tailored web-search prompt (own recency window,
// own source-quality steering, own directive) fired in parallel and merged,
// rather than one blob prompt covering everything — this is what makes
// "5 FASB items" and "5 mental-health items" both actually land on-topic
// instead of the model unevenly splitting attention across a single ask.
const NEWS_WORKER_URL = "https://anthropic-proxy-worker.seancprince.workers.dev";

export const INTEL_CATEGORIES = [
  { id: "fasb", label: "FASB", color: "var(--cyan)", colorText: "var(--cyan-text)", window: 30,
    directive: "Recent FASB activity: newly issued or proposed Accounting Standards Updates (ASUs), exposure drafts open for comment, board meeting decisions, or EITF activity. Prefer fasb.org as a source." },
  { id: "sec", label: "SEC", color: "var(--blue)", colorText: "var(--blue-text)", window: 30,
    directive: "Recent SEC activity relevant to accounting and financial reporting: rule proposals or final rules, enforcement actions against registrants or auditors, statements from the Office of the Chief Accountant, or SEC Regulations Committee updates. Prefer sec.gov as a source." },
  { id: "cpa_cfa_cfe", label: "CPA / CFA / CFE", color: "var(--purple)", colorText: "var(--purple-text)", window: 30,
    directive: "News from the professional bodies governing the CPA, CFA, and CFE credentials: AICPA, CFA Institute, ACFE, NASBA, or state boards of accountancy — exam changes, CPE requirement changes, credentialing news, or professional standards updates. Prefer the official body's own site as a source." },
  { id: "fitness", label: "Physical Fitness", color: "var(--orange)", colorText: "var(--orange-text)", window: 7,
    directive: "Notable fitness, exercise-science, or running/training research and news — new studies on training methodology, injury prevention, recovery, or race coverage. Prefer peer-reviewed research or major outlets (Runner's World, Outside) over generic listicles." },
  { id: "mental_health", label: "Mental Health", color: "var(--green)", colorText: "var(--green-text)", window: 7,
    directive: "Evidence-based mental health research or practical, actionable content — new studies, clinically-grounded techniques, or expert commentary. Prefer peer-reviewed research, APA, or major reputable health outlets over generic self-help content." },
];
const intelCatById = Object.fromEntries(INTEL_CATEGORIES.map(c => [c.id, c]));

// First-run default only — once the user has picked categories once, that
// choice is remembered regardless of which lens tab they're on, so switching
// lenses later never silently resets a deliberate selection.
function defaultIntelCategories(lens) {
  if (lens === "work") return ["fasb", "sec", "cpa_cfa_cfe"];
  if (lens === "growth") return ["fitness", "mental_health"];
  return INTEL_CATEGORIES.map(c => c.id);
}
function loadIntelPrefs(lens) {
  return loadLocal("intel-prefs", null) || { categories: defaultIntelCategories(lens), count: 3 };
}

function buildCategoryPrompt(category, count, seenHeadlines) {
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const skipList = seenHeadlines.length
    ? `\n\nDo not repeat any of these already-shown items — find different, new ones:\n${seenHeadlines.map(h => `- ${h}`).join("\n")}`
    : "";
  return `You are a news curator for Sean Prince — CPA/CFA, Crowe LLP National Office Partner specializing in ASC 606, GMU adjunct accounting professor, father of 8, wellness-focused professional in Northern Virginia.\n\nToday is ${today}.\n\nSearch for and return ${count} genuinely relevant, distinct news item${count > 1 ? "s" : ""} about: ${category.directive}\n\nOnly include items published within the last ${category.window} days. Discard anything older or of uncertain date.${skipList}\n\nReturn exactly ${count} item${count > 1 ? "s" : ""} as a JSON array. Every object MUST match this exact shape, with all keys present and "url" always a real, working link from your search results, never omitted:\n\n[\n  {\n    "headline": "Short headline under 90 characters",\n    "summary": "2-3 sentence summary explaining why this matters.",\n    "source": "Bare publication name, under 30 characters",\n    "url": "https://example.com/actual-article-url"\n  }\n]\n\nReturn only the JSON array. No markdown fences, no preamble, no explanation before or after.`;
}

function CategoryChip({ category, selected, onToggle }) {
  return (
    <button onClick={onToggle} style={{
      padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 600,
      fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap",
      background: selected ? tint(category.color, 13) : "transparent",
      border: `1px solid ${selected ? category.color : "var(--border-soft)"}`,
      color: selected ? category.color : "var(--ink-soft)",
    }}>
      {category.label}
    </button>
  );
}

export function NewsFeed({ lens }) {
  const [items, setItems] = useState(() => loadLocal("news-feed", []));
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(() => loadLocal("news-last-fetch", null));
  const [prefs, setPrefs] = useState(() => loadIntelPrefs(lens));
  const [failedCats, setFailedCats] = useState([]);
  const accent = lensAccent(lens);

  function updatePrefs(next) {
    setPrefs(next);
    saveLocal("intel-prefs", next);
  }
  function toggleCategory(id) {
    const categories = prefs.categories.includes(id)
      ? prefs.categories.filter(c => c !== id)
      : [...prefs.categories, id];
    updatePrefs({ ...prefs, categories });
  }
  function setCount(count) {
    updatePrefs({ ...prefs, count: Math.max(1, Math.min(5, count)) });
  }

  const fetchNews = useCallback(async () => {
    if (prefs.categories.length === 0) return;
    setLoading(true);
    setFailedCats([]);
    const seen = loadLocal("intel-seen", []);
    const seenHeadlines = seen.map(s => s.headline);

    const results = await Promise.allSettled(prefs.categories.map(async (catId) => {
      const category = intelCatById[catId];
      const prompt = buildCategoryPrompt(category, prefs.count, seenHeadlines);
      const res = await fetch(NEWS_WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 2000, tools: [{ type: "web_search_20250305", name: "web_search" }], messages: [{ role: "user", content: prompt }] }) });
      const data = await res.json();
      const allText = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
      const jsonMatch = allText.match(/\[[\s\S]*\]/);
      const parsed = JSON.parse((jsonMatch ? jsonMatch[0] : allText).replace(/```json|```/g, "").trim());
      return parsed.map(item => ({ ...item, categoryId: category.id }));
    }));

    const merged = [];
    const failed = [];
    results.forEach((r, i) => {
      if (r.status === "fulfilled") merged.push(...r.value);
      else failed.push(intelCatById[prefs.categories[i]].label);
    });

    setItems(merged); saveLocal("news-feed", merged);
    setFailedCats(failed);
    const now = new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    setLastFetch(now); saveLocal("news-last-fetch", now);

    // Rolling dedup memory: keep the last 21 days of headlines (capped) so
    // future generations are steered away from repeats without permanently
    // blacklisting a topic that's still genuinely developing.
    const nowTs = Date.now();
    const updatedSeen = [...seen, ...merged.map(m => ({ headline: m.headline, ts: nowTs }))]
      .filter(s => nowTs - s.ts < 21 * 86400000)
      .slice(-80);
    saveLocal("intel-seen", updatedSeen);

    setLoading(false);
  }, [prefs]);

  function clearNews() {
    setItems([]); saveLocal("news-feed", []);
    setLastFetch(null); saveLocal("news-last-fetch", null);
    setFailedCats([]);
  }

  const groupedItems = prefs.categories
    .filter(id => items.some(i => i.categoryId === id))
    .map(id => ({ category: intelCatById[id], items: items.filter(i => i.categoryId === id) }));

  return (
    <div style={{...panel, minWidth:0, overflow:"hidden"}}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div>
          <div style={{ ...monoLabel, marginBottom:2 }}>✦ Curated Intelligence</div>
          {lastFetch && <div style={{fontSize:10,color:"var(--ink-faint)"}}>Last updated {lastFetch}</div>}
        </div>
        {items.length>0 && (
          <button onClick={clearNews} title="Clear the current items" style={{padding:"5px 12px",background:"transparent",border:"1px solid var(--border-soft)",borderRadius:6,color:"var(--ink-soft)",cursor:"pointer",fontSize:11,flexShrink:0}}>Clear</button>
        )}
      </div>

      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
        {INTEL_CATEGORIES.map(cat => (
          <CategoryChip key={cat.id} category={cat} selected={prefs.categories.includes(cat.id)} onToggle={() => toggleCategory(cat.id)} />
        ))}
      </div>

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, marginBottom:14, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{fontSize:11, color:"var(--ink-soft)", fontFamily:"'DM Mono',monospace"}}>Per category</span>
          <button onClick={() => setCount(prefs.count - 1)} style={{width:22,height:22,borderRadius:6,border:"1px solid var(--border-soft)",background:"transparent",color:"var(--ink-soft)",cursor:"pointer",fontSize:14,lineHeight:1}}>−</button>
          <span style={{fontSize:13, fontWeight:600, color:"var(--ink)", width:14, textAlign:"center"}}>{prefs.count}</span>
          <button onClick={() => setCount(prefs.count + 1)} style={{width:22,height:22,borderRadius:6,border:"1px solid var(--border-soft)",background:"transparent",color:"var(--ink-soft)",cursor:"pointer",fontSize:14,lineHeight:1}}>+</button>
        </div>
        <button onClick={fetchNews} disabled={loading || prefs.categories.length===0} style={{padding:"6px 14px",background:prefs.categories.length===0?"var(--border-soft)":accent,border:"none",borderRadius:6,color:prefs.categories.length===0?"var(--ink-faint)":"var(--on-accent)",cursor:prefs.categories.length===0?"default":"pointer",fontSize:12,fontWeight:600,flexShrink:0}}>{loading?"Generating…":"Generate"}</button>
      </div>

      {failedCats.length>0 && (
        <div style={{fontSize:11,color:"var(--amber-text)",marginBottom:10}}>Couldn't load: {failedCats.join(", ")} — try Generate again.</div>
      )}

      {items.length===0
        ? <div style={{fontSize:13,color:"var(--ink-faint)",padding:"20px 0",textAlign:"center"}}>{prefs.categories.length===0 ? "Pick at least one category, then hit Generate." : "Hit Generate to load your curated intelligence."}</div>
        : <div style={{display:"flex",flexDirection:"column",gap:16,minWidth:0}}>
            {groupedItems.map(({category, items: catItems}) => (
              <div key={category.id} style={{minWidth:0}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",color:category.color,marginBottom:8,fontFamily:"'DM Mono',monospace"}}>{category.label}</div>
                <div style={{display:"flex",flexDirection:"column",gap:10,minWidth:0}}>
                  {catItems.map((item,i) => {
                    const color = category.color;
                    const headlineNode = item.url
                      ? <a href={item.url} target="_blank" rel="noopener noreferrer" style={{fontSize:13,fontWeight:600,color:"var(--ink)",lineHeight:1.4,textDecoration:"none",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",minWidth:0}}>{item.headline}</a>
                      : <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",lineHeight:1.4,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden",minWidth:0}}>{item.headline}</div>;
                    return (
                      <div key={i} className="fade-up" style={{background:"var(--panel-soft)",border:"1px solid var(--border)",borderRadius:8,padding:"12px 14px",borderLeft:`3px solid ${color}`,minWidth:0,overflow:"hidden"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:5}}>
                          <div style={{flex:1,minWidth:0}}>{headlineNode}</div>
                          <a
                            href={item.url || undefined}
                            target={item.url ? "_blank" : undefined}
                            rel={item.url ? "noopener noreferrer" : undefined}
                            title={item.source}
                            style={{fontSize:9,padding:"2px 6px",borderRadius:4,flexShrink:0,maxWidth:110,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",background:tint(color, 13),color:color,fontFamily:"'DM Mono',monospace",textDecoration:"none",cursor:item.url?"pointer":"default"}}
                          >{item.source}</a>
                        </div>
                        <div style={{fontSize:12,color:"var(--ink-mid)",lineHeight:1.6}}>{item.summary}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
      }
    </div>
  );
}

// ─── CALENDAR ─────────────────────────────────────────────────────────────────
const COL_W = 118; // fixed px width per day column
const TIME_W = 44; // fixed px width for time label column
const GRID_COLS = `${TIME_W}px repeat(7, ${COL_W}px)`;
const TOTAL_W = TIME_W + COL_W * 7;
const ROW_H = 34; // fixed px height per hour row — the event overlay's math depends on this being exact, not just a minHeight

// Groups a day's timed events into overlap clusters, then greedily assigns
// each event a column within its cluster (same convention every mainstream
// calendar app uses: side-by-side splitting only where events actually
// overlap in time, full width otherwise, rather than stacking or clipping).
function layoutDayEvents(dayEvents) {
  const sorted = [...dayEvents].sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);
  const laidOut = [];
  let cluster = [];
  let clusterEnd = -Infinity;

  function flushCluster() {
    if (cluster.length === 0) return;
    const columnEnds = []; // last endMinutes currently occupying each column
    cluster.forEach((ev) => {
      let col = columnEnds.findIndex((endAt) => endAt <= ev.startMinutes);
      if (col === -1) { col = columnEnds.length; columnEnds.push(ev.endMinutes); }
      else columnEnds[col] = ev.endMinutes;
      ev._col = col;
    });
    const colCount = columnEnds.length;
    cluster.forEach((ev) => { ev._colCount = colCount; laidOut.push(ev); });
    cluster = [];
  }

  sorted.forEach((ev) => {
    if (ev.startMinutes >= clusterEnd) { flushCluster(); clusterEnd = ev.endMinutes; }
    else clusterEnd = Math.max(clusterEnd, ev.endMinutes);
    cluster.push(ev);
  });
  flushCluster();
  return laidOut;
}

export function CalendarView({ events, tasks, lens }) {
  const isMobile = useIsMobile();
  const [weekOffset, setWeekOffset] = useState(0);
  const weekDates = getWeekDates(weekOffset);
  const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const hours = Array.from({length:14},(_,i)=>i+7);
  const todayKey = toDateKey(new Date());
  const accent = lensAccent(lens);
  const accentText = lensAccentText(lens);
  const calEvents = events || [];

  const taskEvents = byLens(tasks||[], lens)
    .filter(t => t.dueDate && t.col !== "Done")
    .map(t => ({ dateKey:t.dueDate, title:t.title, color:isOverdue(t.dueDate)?"var(--red)":isToday(t.dueDate)?"var(--amber)":lensAccent(t.lens), isTask:true, dueDate:t.dueDate }));

  // Mobile-only: which single day of the current week is focused. Day-level
  // paging rather than week-level — crossing past either end just rolls
  // weekOffset along with it, so it reads as one continuous scrub through
  // days rather than two separate navigation concepts.
  const [mobileDayIndex, setMobileDayIndex] = useState(() => {
    const idx = weekDates.findIndex(d => toDateKey(d) === todayKey);
    return idx >= 0 ? idx : 0;
  });
  const goToPrevDay = () => { if (mobileDayIndex === 0) { setWeekOffset(o => o - 1); setMobileDayIndex(6); } else setMobileDayIndex(i => i - 1); };
  const goToNextDay = () => { if (mobileDayIndex === 6) { setWeekOffset(o => o + 1); setMobileDayIndex(0); } else setMobileDayIndex(i => i + 1); };
  const jumpToToday = () => {
    setWeekOffset(0);
    const idx = getWeekDates(0).findIndex(d => toDateKey(d) === todayKey);
    setMobileDayIndex(idx >= 0 ? idx : 0);
  };

  const rangeLabel = `${weekDates[0].toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${weekDates[6].toLocaleDateString("en-US",{month:"short",day:"numeric"})}`;

  // Opens the real event in Google Calendar — the app if it's installed on
  // the phone, mobile/desktop web otherwise. htmlLink comes straight from
  // the Calendar API on every event, read-only access included, so no new
  // OAuth scope or write access was needed for this.
  const openEvent = (e) => { if (e.htmlLink) window.open(e.htmlLink, "_blank", "noopener,noreferrer"); };

  function EventChip({ title, color, time, fullTitle, dashed, onClick }) {
    return (
      <div
        title={fullTitle||title}
        onClick={onClick}
        style={{ background:tint(color, 13), borderLeft:`2px ${dashed?"dashed":"solid"} ${color}`, borderRadius:3, padding:"2px 5px", fontSize:10, color:"var(--ink)", lineHeight:1.35, marginBottom:2, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis", width:"100%", cursor:onClick?"pointer":"default" }}
      >
        {time ? `${time} ` : ""}{trunc(title, 15)}
      </div>
    );
  }

  if (isMobile) {
    const mobileDate = weekDates[mobileDayIndex];
    const mobileDateKey = toDateKey(mobileDate);
    const isTodayFocused = mobileDateKey === todayKey;
    const allDay = calEvents.filter(e => e.dateKey===mobileDateKey && !e.time);
    const dayTasks = taskEvents.filter(t => t.dateKey===mobileDateKey);
    const dayEvents = calEvents.filter(e => e.dateKey===mobileDateKey && e.time && e.startMinutes != null);
    const laidOut = layoutDayEvents(dayEvents);
    const gridStartMin = hours[0]*60;
    const gridEndMin = (hours[hours.length-1]+1)*60;
    const dayLabel = mobileDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

    return (
      <div style={panel}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <div style={{ ...monoLabel, marginBottom:0 }}>Calendar</div>
          {!isTodayFocused && <button onClick={jumpToToday} style={{fontSize:10,padding:"4px 10px",border:"1px solid var(--border)",borderRadius:5,background:"transparent",color:accentText,cursor:"pointer",fontFamily:"'DM Mono',monospace"}}>Today</button>}
        </div>

        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <button onClick={goToPrevDay} style={{width:36,height:36,borderRadius:8,border:"1px solid var(--border)",background:"var(--panel-soft)",color:"var(--ink-mid)",cursor:"pointer",fontSize:16}}>←</button>
          <span style={{fontSize:14,fontWeight:600,color:isTodayFocused?accentText:"var(--ink)",fontFamily:"'DM Mono',monospace"}}>{dayLabel}</span>
          <button onClick={goToNextDay} style={{width:36,height:36,borderRadius:8,border:"1px solid var(--border)",background:"var(--panel-soft)",color:"var(--ink-mid)",cursor:"pointer",fontSize:16}}>→</button>
        </div>

        {(allDay.length > 0 || dayTasks.length > 0) && (
          <div style={{ marginBottom:8, padding:"4px 2px" }}>
            {allDay.map((e,j)=><EventChip key={j} title={e.title} color={e.color} fullTitle={e.title} onClick={e.htmlLink ? () => openEvent(e) : undefined} />)}
            {dayTasks.map((t,j)=><EventChip key={j} title={`📌 ${t.title}`} color={t.color} fullTitle={`Task due: ${dueDayLabel(t.dueDate)} — ${t.title}`} dashed />)}
          </div>
        )}

        <div style={{ position:"relative" }}>
          {hours.map(hour => (
            <div key={hour} style={{display:"grid",gridTemplateColumns:`${TIME_W}px 1fr`}}>
              <div style={{fontSize:9,color:"var(--ink-faint)",textAlign:"right",paddingRight:6,paddingTop:4,fontFamily:"'DM Mono',monospace",lineHeight:1}}>
                {hour<=12?`${hour}am`:`${hour-12}pm`}
              </div>
              <div style={{height:ROW_H,borderTop:"1px solid var(--cal-gridline)",borderLeft:"1px solid var(--cal-gridline)",background:isTodayFocused?"var(--cal-today-row)":hour%2===0?"var(--bg)":"var(--cal-odd-row)"}} />
            </div>
          ))}

          <div style={{ position:"absolute", top:0, left:TIME_W, right:0, height:hours.length*ROW_H, pointerEvents:"none" }}>
            {laidOut.map((e, j) => {
              const clampedStart = Math.max(e.startMinutes, gridStartMin);
              const clampedEnd = Math.min(Math.max(e.endMinutes, clampedStart+15), gridEndMin);
              const top = (clampedStart-gridStartMin)/60*ROW_H;
              const height = Math.max((clampedEnd-clampedStart)/60*ROW_H, 16);
              const widthPct = 100/e._colCount;
              const leftPct = e._col*widthPct;
              return (
                <div
                  key={j}
                  onClick={() => openEvent(e)}
                  style={{ position:"absolute", top, height, left:`calc(${leftPct}% + 2px)`, width:`calc(${widthPct}% - 4px)`, background:tint(e.color, 20), borderLeft:`2px solid ${e.color}`, borderRadius:3, padding:"3px 6px", overflow:"hidden", boxSizing:"border-box", pointerEvents:"auto", cursor:e.htmlLink?"pointer":"default" }}
                >
                  <div style={{fontSize:11,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{trunc(e.title, 22)}</div>
                  {height>=28 && <div style={{fontSize:10,color:"var(--ink-mid)",opacity:0.85}}>{e.time}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={panel}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ ...monoLabel, marginBottom:0 }}>Calendar</div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <button onClick={()=>setWeekOffset(o=>o-1)} style={{width:28,height:28,borderRadius:6,border:"1px solid var(--border)",background:"var(--panel-soft)",color:"var(--ink-mid)",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
          <span style={{fontSize:12,color:"var(--ink-mid)",fontFamily:"'DM Mono',monospace",minWidth:130,textAlign:"center"}}>{rangeLabel}</span>
          <button onClick={()=>setWeekOffset(o=>o+1)} style={{width:28,height:28,borderRadius:6,border:"1px solid var(--border)",background:"var(--panel-soft)",color:"var(--ink-mid)",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>→</button>
          {weekOffset!==0 && <button onClick={()=>setWeekOffset(0)} style={{fontSize:10,padding:"3px 8px",border:"1px solid var(--border)",borderRadius:5,background:"transparent",color:accentText,cursor:"pointer",fontFamily:"'DM Mono',monospace"}}>Today</button>}
        </div>
      </div>

      <div style={{ overflowX:"auto" }}>
        <div style={{ width:TOTAL_W }}>

          {/* Day headers */}
          <div style={{ display:"grid", gridTemplateColumns:GRID_COLS, marginBottom:6 }}>
            <div />
            {weekDates.map((date,i) => {
              const dk = toDateKey(date);
              const isT = dk===todayKey;
              return (
                <div key={i} style={{textAlign:"center",paddingBottom:8,borderBottom:isT?`2px solid ${accent}`:"2px solid var(--border)"}}>
                  <div style={{fontSize:11,fontWeight:700,fontFamily:"'DM Mono',monospace",color:isT?accentText:"var(--ink-mid)"}}>{dayNames[i]}</div>
                  <div style={{fontSize:10,color:isT?accentText:"var(--ink-faint)",marginTop:2,fontFamily:"'DM Mono',monospace"}}>{date.toLocaleDateString("en-US",{month:"numeric",day:"numeric"})}</div>
                </div>
              );
            })}
          </div>

          {/* All-day row */}
          <div style={{ display:"grid", gridTemplateColumns:GRID_COLS, marginBottom:2 }}>
            <div style={{fontSize:9,color:"var(--ink-faint)",textAlign:"right",paddingRight:6,paddingTop:3,fontFamily:"'DM Mono',monospace"}}>all day</div>
            {weekDates.map((date,i) => {
              const dk = toDateKey(date);
              const isT = dk===todayKey;
              const allDay = calEvents.filter(e=>e.dateKey===dk&&!e.time);
              const dayTasks = taskEvents.filter(t=>t.dateKey===dk);
              return (
                <div key={i} style={{borderLeft:"1px solid var(--panel-alt)",borderTop:"1px solid var(--panel-alt)",padding:"2px 3px",minHeight:22,background:isT?"var(--cal-gridline)":"transparent",overflow:"hidden"}}>
                  {allDay.map((e,j)=><EventChip key={j} title={e.title} color={e.color} fullTitle={e.title} onClick={e.htmlLink ? () => openEvent(e) : undefined} />)}
                  {dayTasks.map((t,j)=><EventChip key={j} title={`📌 ${t.title}`} color={t.color} fullTitle={`Task due: ${dueDayLabel(t.dueDate)} — ${t.title}`} dashed />)}
                </div>
              );
            })}
          </div>

          {/* Hour rows: a plain background grid (gridlines + shading only —
             no event chips placed inside individual cells anymore) with a
             duration-aware overlay layered on top per day column. */}
          <div style={{ position: "relative" }}>
            {hours.map(hour => (
              <div key={hour} style={{display:"grid",gridTemplateColumns:GRID_COLS}}>
                <div style={{fontSize:9,color:"var(--ink-faint)",textAlign:"right",paddingRight:6,paddingTop:4,fontFamily:"'DM Mono',monospace",lineHeight:1}}>
                  {hour<=12?`${hour}am`:`${hour-12}pm`}
                </div>
                {weekDates.map((date,i) => {
                  const dk = toDateKey(date);
                  const isT = dk===todayKey;
                  return (
                    <div key={i} style={{height:ROW_H,borderTop:"1px solid var(--cal-gridline)",borderLeft:"1px solid var(--cal-gridline)",background:isT?"var(--cal-today-row)":hour%2===0?"var(--bg)":"var(--cal-odd-row)"}} />
                  );
                })}
              </div>
            ))}

            {weekDates.map((date, i) => {
              const dk = toDateKey(date);
              const dayEvents = calEvents.filter(e => e.dateKey===dk && e.time && e.startMinutes != null);
              const laidOut = layoutDayEvents(dayEvents);
              const gridStartMin = hours[0]*60;
              const gridEndMin = (hours[hours.length-1]+1)*60;
              return (
                <div key={i} style={{ position:"absolute", top:0, left:TIME_W+i*COL_W, width:COL_W, height:hours.length*ROW_H, pointerEvents:"none" }}>
                  {laidOut.map((e, j) => {
                    const clampedStart = Math.max(e.startMinutes, gridStartMin);
                    const clampedEnd = Math.min(Math.max(e.endMinutes, clampedStart+15), gridEndMin);
                    const top = (clampedStart-gridStartMin)/60*ROW_H;
                    const height = Math.max((clampedEnd-clampedStart)/60*ROW_H, 16);
                    const colWidth = (COL_W-4)/e._colCount;
                    const left = 2 + e._col*colWidth;
                    return (
                      <div key={j} onClick={() => openEvent(e)} title={`${e.time} — ${e.title}`} style={{ position:"absolute", top, height, left, width:colWidth-2, background:tint(e.color, 20), borderLeft:`2px solid ${e.color}`, borderRadius:3, padding:"2px 4px", overflow:"hidden", boxSizing:"border-box", pointerEvents:"auto", cursor:e.htmlLink?"pointer":"default" }}>
                        <div style={{fontSize:10,fontWeight:600,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{trunc(e.title, 16)}</div>
                        {height>=26 && <div style={{fontSize:9,color:"var(--ink-mid)",opacity:0.85}}>{e.time}</div>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── KANBAN BOARD ─────────────────────────────────────────────────────────────
const KANBAN_COLS = ["Backlog","In Progress","Done"];
const COL_COLORS  = { Backlog:"var(--ink-faint)", "In Progress":"var(--blue)", Done:"var(--green)" };
const COL_BG      = { Backlog:"var(--panel-soft)",  "In Progress":"var(--info-soft-bg)", Done:"var(--success-soft-bg)" };
const RECURRENCE_LABELS = { daily:"daily", weekly:"weekly", monthly:"monthly", annual:"annually" };
// Backlog visibility window (passed straight into withinDays) per recurrence
// type — daily tasks only need to appear on their actual due day, weekly a
// few days out, monthly matches the plain non-recurring default, annual gets
// extra runway since those often need real lead time to act on.
const RECURRENCE_WINDOW_DAYS = { daily:1, weekly:3, monthly:7, annual:31 };

// A task's recurrence, with fallback for pre-existing tasks that only ever
// had the old `repeat: true` boolean (which always meant weekly).
function taskRecurrence(task) { return task.recurrence || (task.repeat ? "weekly" : null); }

// Next due date computed from the ORIGINAL due date + interval, not from
// whenever it's actually checked off — keeps the task on a fixed cadence
// (e.g. a weekly task due Monday, completed Friday, still lands next Monday)
// rather than drifting based on how late you got to it.
function nextRecurrenceDate(dueDateStr, interval) {
  const d = new Date(dueDateStr+"T12:00:00");
  if (interval==="daily") d.setDate(d.getDate()+1);
  else if (interval==="weekly") d.setDate(d.getDate()+7);
  else if (interval==="monthly") {
    const day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth()+1);
    const lastDay = new Date(d.getFullYear(), d.getMonth()+1, 0).getDate();
    d.setDate(Math.min(day,lastDay)); // clamp e.g. Jan 31 -> Feb 28/29
  } else if (interval==="annual") {
    const month = d.getMonth();
    d.setFullYear(d.getFullYear()+1);
    if (d.getMonth()!==month) d.setDate(0); // Feb 29 -> Feb 28 on non-leap years
  }
  return d.toISOString().split("T")[0];
}

// True if dateStr is unset, overdue, or due within the next `days` days
// (inclusive of today) — powers the Backlog view's default 7-day window.
function withinDays(dateStr, days) {
  if (!dateStr) return true;
  const today = new Date(); today.setHours(0,0,0,0);
  const horizon = new Date(today); horizon.setDate(horizon.getDate()+days-1);
  const d = new Date(dateStr+"T12:00:00"); d.setHours(0,0,0,0);
  return d <= horizon;
}

// Recurring tasks get their own (tighter, type-specific) window. Goal-linked
// tasks (goalId set) come from the Coach as a pre-sequenced batch — e.g. a
// week of daily training-plan tasks all created at once — so they get a
// tight 2-day window too, otherwise the whole batch shows up immediately.
// Plain manually-added tasks (no goalId, no recurrence) keep the original
// flat 7-day default, since those benefit from more advance visibility.
function backlogWindowDays(task) {
  const rec = taskRecurrence(task);
  if (rec) return RECURRENCE_WINDOW_DAYS[rec];
  if (task.goalId) return 2;
  return 7;
}

export function KanbanBoard({ tasks, onTasksChange, lens, defaultTasks=[] }) {
  const isMobile = useIsMobile();
  const [newTask, setNewTask] = useState("");
  const [newDue, setNewDue] = useState("");
  const [newRecurrence, setNewRecurrence] = useState("");
  const [addingTo, setAddingTo] = useState(null);
  const [newLens, setNewLens] = useState(lens==="all"?"work":lens);
  const [showAllBacklog, setShowAllBacklog] = useState(false);
  // Pending delete confirmation for a recurring task — null, or the task
  // itself. Only recurring tasks get a confirmation; plain tasks still
  // delete instantly, unchanged from before.
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const accent = lensAccent(lens);
  const allTasks = tasks||defaultTasks;

  const visible = (lens==="all"?allTasks:allTasks.filter(t=>t.lens===lens)).slice().sort((a,b)=>{
    if(a.col==="Done"&&b.col!=="Done") return 1;
    if(a.col!=="Done"&&b.col==="Done") return -1;
    if(!a.dueDate&&!b.dueDate) return 0;
    if(!a.dueDate) return 1; if(!b.dueDate) return -1;
    return new Date(a.dueDate)-new Date(b.dueDate);
  });
  const doneCount = visible.filter(t=>t.col==="Done").length;

  function addTask(col) {
    if(!newTask.trim()) return;
    const id = Date.now();
    // A recurrence with no due date would silently never fire (moveTask
    // only spawns the next iteration when a due date exists to compute
    // from) — defaulting to today keeps that from being a silent no-op.
    const dueDate = newDue || (newRecurrence ? toDateKey(new Date()) : null);
    onTasksChange([...allTasks,{id,title:newTask.trim(),col,lens:newLens,priority:"Normal",dueDate,recurrence:newRecurrence||null,seriesId:newRecurrence?id:null,goalId:null,phaseId:null}]);
    setNewTask(""); setNewDue(""); setNewRecurrence(""); setAddingTo(null);
  }

  function moveTask(id,toCol) {
    const task = allTasks.find(t=>t.id===id); if(!task) return;
    const interval = taskRecurrence(task);
    if(toCol==="Done"&&interval&&task.dueDate) {
      const nextDue = nextRecurrenceDate(task.dueDate, interval);
      // seriesId is fixed at the first task in the chain and copied to every
      // spawned iteration, so "delete the whole series" has something
      // concrete to filter on. Falls back to the task's own id for
      // pre-existing recurring tasks that predate this field.
      const seriesId = task.seriesId || task.id;
      onTasksChange([...allTasks.map(t=>t.id===id?{...t,col:toCol}:t),{...task,id:Date.now()+1,col:"Backlog",dueDate:nextDue,recurrence:interval,seriesId}]);
    } else { onTasksChange(allTasks.map(t=>t.id===id?{...t,col:toCol}:t)); }
  }

  // Recurring tasks get a confirm step (this occurrence vs. the whole
  // series); plain tasks still delete instantly like before.
  function requestDelete(task) {
    if (taskRecurrence(task)) setDeleteConfirm(task);
    else onTasksChange(allTasks.filter(t=>t.id!==task.id));
  }
  function deleteOccurrence() {
    onTasksChange(allTasks.filter(t=>t.id!==deleteConfirm.id));
    setDeleteConfirm(null);
  }
  function deleteSeries() {
    const sid = deleteConfirm.seriesId || deleteConfirm.id;
    onTasksChange(allTasks.filter(t=>(t.seriesId||t.id)!==sid));
    setDeleteConfirm(null);
  }

  // Scoped to whatever's currently visible (i.e. the active lens tab) —
  // clicking this on "Work" only clears Work's Done tasks, not every lens.
  function deleteAllDone() {
    const doneIds = new Set(visible.filter(t=>t.col==="Done").map(t=>t.id));
    onTasksChange(allTasks.filter(t=>!doneIds.has(t.id)));
  }

  function dueBadge(task) {
    if(!task.dueDate||task.col==="Done") return null;
    if(isOverdue(task.dueDate)) return <span style={{fontSize:9,padding:"2px 6px",borderRadius:4,background:tint("var(--red)", 19),color:"var(--danger-badge-text)",fontFamily:"'DM Mono',monospace",fontWeight:700,border:`1px solid ${tint("var(--red)", 31)}`}}>⚠ OVERDUE · {dueDayLabel(task.dueDate)}</span>;
    if(isToday(task.dueDate)) return <span style={{fontSize:9,padding:"2px 6px",borderRadius:4,background:tint("var(--amber)", 19),color:"var(--warning-badge-text)",fontFamily:"'DM Mono',monospace",fontWeight:700,border:`1px solid ${tint("var(--amber)", 31)}`}}>● DUE TODAY</span>;
    return <span style={{fontSize:9,color:"var(--ink-soft)",fontFamily:"'DM Mono',monospace"}}>Due {dueDayLabel(task.dueDate)}</span>;
  }

  function cardExtra(task) {
    if(task.col==="Done"||!task.dueDate) return {};
    if(isOverdue(task.dueDate)) return {borderColor:"var(--red)",boxShadow:`0 0 0 1px ${tint("var(--red)", 19)}`};
    if(isToday(task.dueDate)) return {borderColor:"var(--amber)",boxShadow:`0 0 0 1px ${tint("var(--amber)", 19)}`};
    return {};
  }

  return (
    <div style={panel}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:12}}>
        <div style={{...monoLabel,marginBottom:0}}>Tasks</div>
        {doneCount>0 && (
          <button onClick={deleteAllDone} title="Delete every task currently in Done" style={{padding:"4px 10px",background:"transparent",border:"1px solid var(--danger-soft-border)",borderRadius:6,cursor:"pointer",color:"var(--red-text)",fontSize:11,fontFamily:"'DM Sans',sans-serif"}}>Delete Done Tasks</button>
        )}
      </div>
      <div style={{display:"grid",gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",gap:10}}>
        {KANBAN_COLS.map(col => {
          const colTasks = visible.filter(t=>t.col===col);
          // Backlog defaults to a visibility window per task (7 days for
          // plain tasks, tighter/looser per recurrence type — see
          // RECURRENCE_WINDOW_DAYS) so it doesn't get bogged down with
          // things not yet actionable. Anything overdue or undated always
          // shows. Doesn't touch In Progress or Done — those stay unfiltered.
          const displayTasks = col==="Backlog" && !showAllBacklog ? colTasks.filter(t=>withinDays(t.dueDate,backlogWindowDays(t))) : colTasks;
          const hiddenCount = colTasks.length - displayTasks.length;
          return (
            <div key={col} style={{background:COL_BG[col],borderRadius:8,padding:10}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:COL_COLORS[col]}}/>
                <span style={{fontSize:11,fontWeight:700,color:"var(--ink-mid)",fontFamily:"'DM Mono',monospace"}}>{col}</span>
                <span style={{fontSize:10,color:"var(--ink-faint)",marginLeft:"auto"}}>{colTasks.length}</span>
              </div>
              {col==="Backlog" && (
                <label style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:"var(--ink-faint)",marginBottom:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={showAllBacklog} onChange={e=>setShowAllBacklog(e.target.checked)} style={{accentColor:accent}}/>
                  Show all{!showAllBacklog && hiddenCount>0 ? ` (${hiddenCount} hidden)` : ""}
                </label>
              )}
              {displayTasks.map(task => (
                <div key={task.id} style={{background:"var(--bg)",border:"1px solid var(--border)",borderRadius:6,padding:"8px 10px",marginBottom:6,borderLeft:`2px solid ${lensAccent(task.lens)}`,...cardExtra(task)}}>
                  <div style={{fontSize:12,color:"var(--ink)",lineHeight:1.5,marginBottom:5,display:"flex",alignItems:"flex-start",gap:7}}>
                    {col!=="Done" && (
                      <input type="checkbox" checked={false} onChange={()=>moveTask(task.id,"Done")} title="Mark done" style={{marginTop:2,accentColor:accent,cursor:"pointer",flexShrink:0}}/>
                    )}
                    <span>
                      {task.title}
                      {taskRecurrence(task)&&<span title={`Repeats ${RECURRENCE_LABELS[taskRecurrence(task)]}`} style={{marginLeft:6,fontSize:10,color:"var(--ink-faint)"}}>↺</span>}
                    </span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:4,marginBottom:6}}>
                    <span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:tint(lensAccent(task.lens), 13),color:lensAccent(task.lens),fontFamily:"'DM Mono',monospace"}}>{lensLabel(task.lens)}</span>
                    {task.goalId && <span title="Linked to a goal — see the Goals widget" style={{fontSize:10}}>🎯</span>}
                    {dueBadge(task)}
                  </div>
                  <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                    {KANBAN_COLS.filter(c=>c!==col&&c!=="Done").map(c=><button key={c} onClick={()=>moveTask(task.id,c)} style={{fontSize:9,padding:"2px 6px",border:"1px solid var(--border-soft)",borderRadius:3,background:"transparent",cursor:"pointer",color:"var(--ink-soft)"}}>→ {c}</button>)}
                    <button onClick={()=>requestDelete(task)} style={{fontSize:9,padding:"2px 6px",border:"1px solid var(--danger-soft-border)",borderRadius:3,background:"transparent",cursor:"pointer",color:"var(--red-text)",marginLeft:"auto"}}>✕</button>
                  </div>
                </div>
              ))}
              {addingTo===col ? (
                <div>
                  <input autoFocus value={newTask} onChange={e=>setNewTask(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addTask(col)} placeholder="Task name…" style={{width:"100%",background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"6px 8px",color:"var(--ink)",fontSize:12,marginBottom:6,outline:"none",fontFamily:"'DM Sans',sans-serif"}}/>
                  <input type="date" value={newDue} onChange={e=>setNewDue(e.target.value)} style={{width:"100%",background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"5px 8px",color:"var(--ink-mid)",fontSize:11,marginBottom:6,outline:"none",fontFamily:"'DM Mono',monospace"}}/>
                  <div style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
                    <select value={newLens} onChange={e=>setNewLens(e.target.value)} style={{flex:1,background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"5px 6px",color:"var(--ink-mid)",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
                      {LENSES.filter(l=>l.id!=="all").map(l=><option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                    <select value={newRecurrence} onChange={e=>{const v=e.target.value;setNewRecurrence(v);if(v&&!newDue)setNewDue(toDateKey(new Date()));}} style={{flex:1,background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"5px 6px",color:"var(--ink-mid)",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
                      <option value="">No repeat</option>
                      <option value="daily">Daily ↺</option>
                      <option value="weekly">Weekly ↺</option>
                      <option value="monthly">Monthly ↺</option>
                      <option value="annual">Annual ↺</option>
                    </select>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>addTask(col)} style={{flex:1,padding:"5px",background:accent,border:"none",borderRadius:5,color:"var(--on-accent)",cursor:"pointer",fontSize:11,fontWeight:600}}>Add</button>
                    <button onClick={()=>setAddingTo(null)} style={{flex:1,padding:"5px",background:"var(--panel-alt)",border:"none",borderRadius:5,color:"var(--ink-soft)",cursor:"pointer",fontSize:11}}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={()=>{setAddingTo(col);setNewLens(lens==="all"?"work":lens);}} style={{width:"100%",padding:"6px",border:"1px dashed var(--border-soft)",borderRadius:5,background:"transparent",cursor:"pointer",color:"var(--ink-faint)",fontSize:11}}>+ Add task</button>
              )}
            </div>
          );
        })}
      </div>
      {deleteConfirm && (
        <ConfirmRecurringDeleteModal
          task={deleteConfirm}
          onOccurrence={deleteOccurrence}
          onSeries={deleteSeries}
          onCancel={()=>setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}

// Only recurring tasks route through this — a plain task still deletes
// instantly, no confirm. "Delete this and stop the series" removes every
// task sharing this one's seriesId (past Done history and, if this is a
// completed instance, the currently-pending next occurrence too).
function ConfirmRecurringDeleteModal({ task, onOccurrence, onSeries, onCancel }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ position:"fixed", inset:0, background:"var(--overlay)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
      <div style={{ background:"var(--panel-soft)", border:"1px solid var(--border)", borderRadius:10, padding:20, maxWidth:380, width:"100%" }}>
        <div style={{ fontSize:isMobile?15:14, fontWeight:600, color:"var(--ink)", marginBottom:10 }}>Delete "{task.title}"?</div>
        <div style={{ fontSize:isMobile?13:12, color:"var(--ink-soft)", marginBottom:16, lineHeight:1.6 }}>
          This task repeats {RECURRENCE_LABELS[taskRecurrence(task)]}. Delete just this one, or stop the whole series?
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <button onClick={onOccurrence} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:"1px solid var(--border)", borderRadius:6, color:"var(--ink)", cursor:"pointer", fontSize:isMobile?13:12, textAlign:"left" }}>
            Delete just this occurrence
          </button>
          <button onClick={onSeries} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:`1px solid ${tint("var(--red)", 40)}`, borderRadius:6, color:"var(--red-text)", cursor:"pointer", fontSize:isMobile?13:12, textAlign:"left" }}>
            Delete this and stop the series
          </button>
          <button onClick={onCancel} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:"none", borderRadius:6, color:"var(--ink-faint)", cursor:"pointer", fontSize:isMobile?13:12 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── GOALS TRACKER ────────────────────────────────────────────────────────────
export function GoalsTracker({ lens, goals, tasks, onGoalsChange, onApplyGoalPlan, onDeleteGoal, onRemovePhase, onApplyGoalDelta, onReschedulePhase, onReorderPhase, onCompletePhase }) {
  const isMobile = useIsMobile();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({title:"",target:"",unit:"",progress:"0",dueDate:"",lens:lens==="all"?"work":lens});
  const [coachTarget, setCoachTarget] = useState(null); // null | "new" | goalId — which goal (if any) the Coach panel is open for
  // What's pending a delete confirmation — null, or { kind: "goal"|"phase", goalId, phaseId?, label, linkedCount }.
  // Deletion never silently orphans or silently wipes linked tasks — the
  // person always explicitly picks which, via ConfirmDeleteModal below.
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Which phase's inline date-edit control is currently open — rescheduling
  // shifts that phase's linked tasks by the same number of days, so it's
  // handled as a direct edit rather than a Coach round-trip (see App.js).
  const [reschedulingPhase, setReschedulingPhase] = useState(null);
  const accent = lensAccent(lens);
  const accentText = lensAccentText(lens);
  const allGoals = goals||[];
  const allTasks = tasks||[];
  const active = byLens(allGoals,lens).filter(g=>!g.complete);
  const completed = byLens(allGoals,lens).filter(g=>g.complete);

  function addGoal() {
    if(!form.title.trim()||!form.target) return;
    onGoalsChange([...allGoals,{id:Date.now(),...form,target:Number(form.target),progress:Number(form.progress),complete:false,progressMode:"manual",phases:[]}]);
    setForm({title:"",target:"",unit:"",progress:"0",dueDate:"",lens:lens==="all"?"work":lens}); setAdding(false);
  }
  function updateProgress(id,val) { onGoalsChange(allGoals.map(g=>g.id===id?{...g,progress:Math.min(Number(val),g.target)}:g)); }
  function completeGoal(id) { onGoalsChange(allGoals.map(g=>g.id===id?{...g,complete:true}:g)); }
  function reopenGoal(id) { onGoalsChange(allGoals.map(g=>g.id===id?{...g,complete:false}:g)); }
  function askDeleteGoal(goal) {
    setDeleteConfirm({ kind:"goal", goalId:goal.id, label:`"${goal.title}"`, linkedCount: allTasks.filter(t=>t.goalId===goal.id).length });
  }


  // For "tasks"-mode goals, progress is derived live from linked tasks
  // rather than a number you bump yourself — the plan the Coach built IS
  // the progress tracker, so there's nothing separate to keep in sync.
  function goalStats(goal) {
    const linked = allTasks.filter(t => t.goalId === goal.id);
    if (goal.progressMode === "tasks") {
      const done = linked.filter(t => t.col === "Done").length;
      const pct = linked.length ? Math.round((done/linked.length)*100) : 0;
      return { pct, label: `${done} of ${linked.length} tasks done`, linked };
    }
    const pct = goal.target ? Math.min(100, Math.round((goal.progress/goal.target)*100)) : 0;
    return { pct, label: `${goal.progress} / ${goal.target} ${goal.unit||""}`, linked };
  }

  return (
    <div style={panel}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div style={{...monoLabel,marginBottom:0}}>Goals & Progress</div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>{setCoachTarget("new");setAdding(false);}} style={{padding:"4px 12px",background:coachTarget==="new"?"var(--panel-alt)":accent,border:"none",borderRadius:6,cursor:"pointer",color:coachTarget==="new"?"var(--ink-soft)":"var(--on-accent)",fontSize:11,fontWeight:600}}>{coachTarget==="new"?"Cancel":"🎯 New Goal (Coach)"}</button>
          <button onClick={()=>{setAdding(a=>!a);setCoachTarget(null);}} style={{padding:"4px 12px",background:adding?"var(--panel-alt)":"transparent",border:`1px solid ${tint(accent, 40)}`,borderRadius:6,cursor:"pointer",color:adding?"var(--ink-soft)":accentText,fontSize:11,fontWeight:600}}>{adding?"Cancel":"+ Quick Add"}</button>
        </div>
      </div>
      {adding && (
        <div style={{background:"var(--panel-soft)",border:"1px solid var(--border)",borderRadius:8,padding:12,marginBottom:14,display:"flex",flexDirection:"column",gap:8}}>
          <input placeholder="Goal title" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={{background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"7px 10px",color:"var(--ink)",fontSize:12,outline:"none",fontFamily:"'DM Sans',sans-serif"}}/>
          <div style={{display:"grid",gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",gap:8}}>
            <input placeholder="Target (e.g. 10)" value={form.target} onChange={e=>setForm(f=>({...f,target:e.target.value}))} style={{background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"7px 10px",color:"var(--ink)",fontSize:12,outline:"none",fontFamily:"'DM Sans',sans-serif"}}/>
            <input placeholder="Unit (e.g. books)" value={form.unit} onChange={e=>setForm(f=>({...f,unit:e.target.value}))} style={{background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"7px 10px",color:"var(--ink)",fontSize:12,outline:"none",fontFamily:"'DM Sans',sans-serif"}}/>
            <input type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} style={{background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"7px 10px",color:"var(--ink-mid)",fontSize:12,outline:"none",fontFamily:"'DM Mono',monospace"}}/>
          </div>
          <select value={form.lens} onChange={e=>setForm(f=>({...f,lens:e.target.value}))} style={{background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:5,padding:"7px 10px",color:"var(--ink-mid)",fontSize:12,outline:"none",fontFamily:"'DM Mono',monospace"}}>
            {LENSES.filter(l=>l.id!=="all").map(l=><option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
          <button onClick={addGoal} style={{padding:"7px",background:accent,border:"none",borderRadius:6,color:"var(--on-accent)",cursor:"pointer",fontSize:12,fontWeight:600}}>Save Goal</button>
        </div>
      )}
      {coachTarget==="new" && (
        <GoalsCoachPanel
          goal={null}
          goals={allGoals}
          tasks={allTasks}
          lens={lens}
          onApply={(plan)=>{onApplyGoalPlan(plan,null);setCoachTarget(null);}}
          onClose={()=>setCoachTarget(null)}
        />
      )}
      {active.length===0
        ? <div style={{fontSize:13,color:"var(--ink-faint)",padding:"20px 0",textAlign:"center"}}>No active goals for this lens. Add one above.</div>
        : <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {active.map(goal => {
              const { pct, label } = goalStats(goal);
              const ga = lensAccent(goal.lens);
              const gaText = lensAccentText(goal.lens);
              const barColor = pct>=80?"var(--green)":pct>=50?"var(--amber)":ga;
              const isTasksMode = goal.progressMode === "tasks";
              return (
                <div key={goal.id}>
                  <div style={{background:"var(--panel-soft)",border:"1px solid var(--border)",borderRadius:8,padding:"12px 14px",borderLeft:`3px solid ${ga}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,flexWrap:"wrap",gap:6}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500,color:"var(--ink)"}}>{goal.title}</div>
                        <div style={{fontSize:11,color:"var(--ink-soft)",marginTop:2}}>
                          {label}
                          {goal.dueDate&&` · Due ${dueDayLabel(goal.dueDate)}`}
                          <span style={{marginLeft:6,padding:"1px 5px",borderRadius:3,background:tint(ga,13),color:gaText,fontSize:9,fontFamily:"'DM Mono',monospace"}}>{lensLabel(goal.lens)}</span>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6,alignItems:"center"}}>
                        <span style={{fontSize:13,fontWeight:600,color:barColor,fontFamily:"'DM Mono',monospace"}}>{pct}%</span>
                        <button onClick={()=>setCoachTarget(t=>t===goal.id?null:goal.id)} title="Break this down with the Coach" style={{fontSize:isMobile?13:10,padding:isMobile?"6px 10px":"2px 7px",border:`1px solid ${tint(accent, 40)}`,borderRadius:4,background:coachTarget===goal.id?accent:"transparent",cursor:"pointer",color:coachTarget===goal.id?"var(--on-accent)":accentText}}>🎯</button>
                        <button onClick={()=>completeGoal(goal.id)} title="Mark complete" style={{fontSize:isMobile?13:10,padding:isMobile?"6px 10px":"2px 7px",border:`1px solid ${tint("var(--green)", 27)}`,borderRadius:4,background:"transparent",cursor:"pointer",color:"var(--green-text)"}}>✓</button>
                        <button onClick={()=>askDeleteGoal(goal)} title="Delete" style={{fontSize:isMobile?13:10,padding:isMobile?"6px 10px":"2px 7px",border:"1px solid var(--danger-soft-border)",borderRadius:4,background:"transparent",cursor:"pointer",color:"var(--red-text)"}}>✕</button>
                      </div>
                    </div>
                    <div style={{height:5,background:"var(--panel-alt)",borderRadius:3,overflow:"hidden",marginBottom:8}}>
                      <div style={{width:`${pct}%`,height:"100%",background:barColor,borderRadius:3,transition:"width 0.5s ease"}}/>
                    </div>

                    {isTasksMode ? (
                      <div style={{fontSize:11,color:"var(--ink-faint)"}}>Progress updates automatically as linked tasks are marked Done.</div>
                    ) : (
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:11,color:"var(--ink-soft)"}}>Update:</span>
                        <input type="number" min="0" max={goal.target} value={goal.progress} onChange={e=>updateProgress(goal.id,e.target.value)} style={{width:70,background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:4,padding:"3px 7px",color:"var(--ink)",fontSize:12,outline:"none",fontFamily:"'DM Mono',monospace"}}/>
                        <span style={{fontSize:11,color:"var(--ink-soft)"}}>{goal.unit}</span>
                      </div>
                    )}

                    {goal.phases?.length>0 && (
                      <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--panel-alt)",display:"flex",flexDirection:"column",gap:isMobile?10:5}}>
                        {goal.phases.map((phase,i) => {
                          const phaseTasks = allTasks.filter(t=>t.phaseId===phase.id);
                          const phaseDone = phaseTasks.filter(t=>t.col==="Done").length;
                          const isRescheduling = reschedulingPhase===phase.id;
                          return (
                            <div key={phase.id||i}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11,color:"var(--ink-soft)",flexWrap:"wrap",gap:4}}>
                                <span style={{display:"flex",alignItems:"center",gap:isMobile?6:4}}>
                                  <span style={{display:"flex",flexDirection:"column",gap:isMobile?2:0}}>
                                    <button onClick={()=>onReorderPhase(goal.id,phase.id,"up")} disabled={i===0} title="Move earlier" style={{background:"transparent",border:"none",color:i===0?"var(--ink-disabled)":"var(--ink-faint)",cursor:i===0?"default":"pointer",fontSize:isMobile?14:9,lineHeight:1,padding:isMobile?4:0}}>▲</button>
                                    <button onClick={()=>onReorderPhase(goal.id,phase.id,"down")} disabled={i===goal.phases.length-1} title="Move later" style={{background:"transparent",border:"none",color:i===goal.phases.length-1?"var(--ink-disabled)":"var(--ink-faint)",cursor:i===goal.phases.length-1?"default":"pointer",fontSize:isMobile?14:9,lineHeight:1,padding:isMobile?4:0}}>▼</button>
                                  </span>
                                  <span style={{fontSize:isMobile?13:11,color:phase.complete?"var(--green-text)":undefined,textDecoration:phase.complete?"line-through":"none"}}>Phase {i+1}: {phase.title}</span>
                                </span>
                                <span style={{display:"flex",alignItems:"center",gap:isMobile?4:6}}>
                                  <span style={{fontFamily:"'DM Mono',monospace",color:"var(--ink-faint)",fontSize:isMobile?11:undefined}}>
                                    {phaseTasks.length>0?`${phaseDone}/${phaseTasks.length}`:""}{phase.dueDate?` · ${dueDayLabel(phase.dueDate)}`:""}
                                  </span>
                                  {phase.complete ? (
                                    <span title="Phase complete" style={{color:"var(--green-text)",fontSize:isMobile?15:11,padding:isMobile?6:2}}>✓</span>
                                  ) : (
                                    <button
                                      onClick={()=>onCompletePhase(goal.id,phase.id)}
                                      title="Mark this phase complete (marks its tasks Done too)"
                                      style={{background:"transparent",border:"none",color:"var(--ink-faint)",cursor:"pointer",fontSize:isMobile?15:11,padding:isMobile?6:2}}
                                    >✓</button>
                                  )}
                                  <button
                                    onClick={()=>setReschedulingPhase(isRescheduling?null:phase.id)}
                                    title="Reschedule this phase (shifts its linked tasks too)"
                                    style={{background:"transparent",border:"none",color:isRescheduling?accentText:"var(--ink-faint)",cursor:"pointer",fontSize:isMobile?15:11,padding:isMobile?6:2}}
                                  >🗓</button>
                                  <button
                                    onClick={()=>setDeleteConfirm({kind:"phase",goalId:goal.id,phaseId:phase.id,label:`Phase ${i+1}: "${phase.title}"`,linkedCount:phaseTasks.length})}
                                    title="Remove this phase"
                                    style={{background:"transparent",border:"none",color:"var(--ink-faint)",cursor:"pointer",fontSize:isMobile?15:11,padding:isMobile?6:2}}
                                  >✕</button>
                                </span>
                              </div>
                              {isRescheduling && (
                                <div style={{display:"flex",alignItems:"center",gap:6,marginTop:6,marginLeft:isMobile?0:14,flexWrap:"wrap"}}>
                                  <input
                                    type="date"
                                    defaultValue={phase.dueDate||""}
                                    id={`reschedule-${phase.id}`}
                                    style={{background:"var(--bg)",border:"1px solid var(--border-soft)",borderRadius:4,padding:isMobile?"7px 9px":"3px 7px",color:"var(--ink-mid)",fontSize:isMobile?13:11,outline:"none",fontFamily:"'DM Mono',monospace"}}
                                  />
                                  <button
                                    onClick={()=>{
                                      const val = document.getElementById(`reschedule-${phase.id}`).value;
                                      if (val) onReschedulePhase(goal.id,phase.id,val);
                                      setReschedulingPhase(null);
                                    }}
                                    style={{fontSize:isMobile?12:10,padding:isMobile?"7px 12px":"3px 9px",background:accent,border:"none",borderRadius:4,color:"var(--on-accent)",cursor:"pointer",fontWeight:600}}
                                  >{isMobile?"Save":"Save — shifts linked tasks too"}</button>
                                  <button onClick={()=>setReschedulingPhase(null)} style={{fontSize:isMobile?12:10,padding:isMobile?"7px 12px":"3px 9px",background:"transparent",border:"1px solid var(--border)",borderRadius:4,color:"var(--ink-soft)",cursor:"pointer"}}>Cancel</button>
                                  {isMobile && <div style={{fontSize:10,color:"var(--ink-faint)",width:"100%"}}>Shifts this phase's linked tasks by the same amount.</div>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {coachTarget===goal.id && (
                    <div style={{marginTop:8}}>
                      <GoalsCoachPanel
                        goal={goal}
                        goals={allGoals}
                        tasks={allTasks}
                        lens={lens}
                        onApply={(plan)=>{onApplyGoalPlan(plan,goal.id);setCoachTarget(null);}}
                        onApplyDelta={(kind,payload)=>onApplyGoalDelta(goal.id,kind,payload)}
                        onClose={()=>setCoachTarget(null)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
      }
      {completed.length>0 && (
        <div style={{marginTop:20}}>
          <div style={{...monoLabel,color:"var(--green-text)",marginBottom:10}}>✓ Completed</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {completed.map(goal => (
              <div key={goal.id} style={{background:"var(--success-soft-bg)",border:"1px solid var(--success-soft-border)",borderRadius:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",opacity:0.75}}>
                <div>
                  <div style={{fontSize:12,color:"var(--ink-soft)",textDecoration:"line-through"}}>{goal.title}</div>
                  <div style={{fontSize:10,color:"var(--ink-faint)",marginTop:2}}>
                    {goal.target} {goal.unit}{goal.dueDate&&` · Due ${dueDayLabel(goal.dueDate)}`}
                    <span style={{marginLeft:6,padding:"1px 5px",borderRadius:3,background:tint(lensAccent(goal.lens), 13),color:lensAccent(goal.lens),fontSize:9,fontFamily:"'DM Mono',monospace"}}>{lensLabel(goal.lens)}</span>
                  </div>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{fontSize:10,color:"var(--green-text)",fontFamily:"'DM Mono',monospace"}}>✓ DONE</span>
                  <button onClick={()=>reopenGoal(goal.id)} style={{fontSize:9,padding:"2px 7px",border:"1px solid var(--border)",borderRadius:4,background:"transparent",cursor:"pointer",color:"var(--ink-soft)"}}>Reopen</button>
                  <button onClick={()=>askDeleteGoal(goal)} style={{fontSize:9,padding:"2px 7px",border:"1px solid var(--danger-soft-border)",borderRadius:4,background:"transparent",cursor:"pointer",color:"var(--red-text)"}}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {deleteConfirm && (
        <ConfirmDeleteModal
          title={deleteConfirm.kind==="goal" ? `Delete goal ${deleteConfirm.label}?` : `Remove ${deleteConfirm.label}?`}
          itemLabel={deleteConfirm.kind==="goal" ? "This goal" : "This phase"}
          linkedCount={deleteConfirm.linkedCount}
          onCascade={()=>{
            if (deleteConfirm.kind==="goal") onDeleteGoal(deleteConfirm.goalId, true);
            else onRemovePhase(deleteConfirm.goalId, deleteConfirm.phaseId, true);
            setDeleteConfirm(null);
          }}
          onKeepTasks={()=>{
            if (deleteConfirm.kind==="goal") onDeleteGoal(deleteConfirm.goalId, false);
            else onRemovePhase(deleteConfirm.goalId, deleteConfirm.phaseId, false);
            setDeleteConfirm(null);
          }}
          onCancel={()=>setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}

// Reusable delete confirmation — used for both deleting a goal and removing
// a single phase. When there are linked tasks, it makes the person choose
// explicitly rather than either silently orphaning them or silently wiping
// them; with nothing linked, it's just a plain single confirm.
function ConfirmDeleteModal({ title, itemLabel, linkedCount, onCascade, onKeepTasks, onCancel }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ position:"fixed", inset:0, background:"var(--overlay)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
      <div style={{ background:"var(--panel-soft)", border:"1px solid var(--border)", borderRadius:10, padding:20, maxWidth:380, width:"100%" }}>
        <div style={{ fontSize:isMobile?15:14, fontWeight:600, color:"var(--ink)", marginBottom:10 }}>{title}</div>
        {linkedCount > 0 ? (
          <>
            <div style={{ fontSize:isMobile?13:12, color:"var(--ink-soft)", marginBottom:16, lineHeight:1.6 }}>
              {itemLabel} has {linkedCount} linked task{linkedCount===1?"":"s"}. What do you want to do with {linkedCount===1?"it":"them"}?
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <button onClick={onKeepTasks} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:"1px solid var(--border)", borderRadius:6, color:"var(--ink)", cursor:"pointer", fontSize:isMobile?13:12, textAlign:"left" }}>
                Delete this only — keep the {linkedCount===1?"task":"tasks"} on the board
              </button>
              <button onClick={onCascade} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:`1px solid ${tint("var(--red)", 40)}`, borderRadius:6, color:"var(--red-text)", cursor:"pointer", fontSize:isMobile?13:12, textAlign:"left" }}>
                Delete this and its {linkedCount} task{linkedCount===1?"":"s"}
              </button>
              <button onClick={onCancel} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:"none", borderRadius:6, color:"var(--ink-faint)", cursor:"pointer", fontSize:isMobile?13:12 }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize:isMobile?13:12, color:"var(--ink-soft)", marginBottom:16 }}>This can't be undone.</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={onCascade} style={{ flex:1, padding:isMobile?11:8, background:"var(--red)", border:"none", borderRadius:6, color:"var(--on-accent)", cursor:"pointer", fontSize:isMobile?13:12, fontWeight:600 }}>Delete</button>
              <button onClick={onCancel} style={{ flex:1, padding:isMobile?11:8, background:"transparent", border:"1px solid var(--border)", borderRadius:6, color:"var(--ink-soft)", cursor:"pointer", fontSize:isMobile?13:12 }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── GOALS COACH ──────────────────────────────────────────────────────────────
// Same proven pattern as the Meal Planner's Ask AI: free-form chat for the
// back-and-forth, an explicit "build my plan" action that requests forced
// JSON once there's enough context, a preview, then an explicit Apply step.
// Nothing commits until Apply is clicked.
async function callWorker(messages, maxTokens = 1200) {
  const res = await fetch("https://anthropic-proxy-worker.seancprince.workers.dev", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: maxTokens, messages }),
  });
  if (!res.ok) throw new Error(`Worker returned ${res.status}`);
  const data = await res.json();
  return (data.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
}

function extractJSON(rawText) {
  try { return JSON.parse(rawText); } catch {}
  const objMatch = rawText.match(/\{[\s\S]*\}/);
  if (objMatch) { try { return JSON.parse(objMatch[0]); } catch {} }
  return null;
}

function GoalsCoachPanel({ goal, goals, tasks, lens, onApply, onApplyDelta, onClose }) {
  const isMobile = useIsMobile();
  const accent = lensAccent(lens);
  const accentText = lensAccentText(lens);
  const isNew = !goal;
  const [messages, setMessages] = useState([{
    role: "assistant",
    text: isNew
      ? "Hey! Let's set up a new goal together. What are you working toward?"
      : `Let's dig into "${goal.title}". Want to break this into phases, adjust the deadline, or figure out next steps? Tell me where you're at.`,
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposedPlan, setProposedPlan] = useState(null);
  // Edit mode, for an existing goal only: "chat" (default — free chat, or a
  // full replan via buildPlan) | "add-phase" | "add-tasks" (both scoped
  // additions via buildDelta, safer than asking the model to reproduce the
  // whole goal untouched except one change).
  const [mode, setMode] = useState("chat");
  const [selectedPhaseId, setSelectedPhaseId] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [proposedDelta, setProposedDelta] = useState(null); // { kind, payload }
  const messagesEndRef = useRef(null);

  // Auto-scroll the chat to the latest message, so a new reply doesn't
  // require manually scrolling down to see it.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  // No calendar-write capability exists in this app, so the Coach is
  // explicitly told to route anything schedule-shaped through Tasks
  // instead of proposing calendar events — matches how Sean wants this
  // handled rather than waiting on a bigger OAuth scope change.
  function buildContext() {
    const goalLine = isNew
      ? "This is a brand new goal — nothing exists yet."
      : `Existing goal: "${goal.title}" — ${goal.progressMode === "tasks" ? "tracked by linked tasks" : `${goal.progress}/${goal.target} ${goal.unit || ""}`}${goal.dueDate ? `, due ${goal.dueDate}` : ""}. Existing phases: ${goal.phases?.length ? goal.phases.map(p => p.title).join(", ") : "none yet"}.`;
    const otherGoals = (goals || []).filter(g => !goal || g.id !== goal.id).filter(g => !g.complete).map(g => g.title).join(", ") || "none";
    return `You are a warm, energetic, genuinely encouraging goals coach inside Sean Prince's personal command center. Sean is a CPA/CFA, a Crowe LLP National Office partner, a GMU adjunct professor, and a father of eight, based in Northern Virginia — but this is his personal growth space, so talk to him like a coach who's in his corner, not like an accountant. A few warm, concise sentences at a time, not paragraphs.

${goalLine}
Sean's other active goals right now: ${otherGoals}
Current lens: ${lens}
Today's date: ${new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})} — use this as the actual current date for every due date you propose, not whatever year you'd otherwise assume.

CRITICAL: this app has no calendar-write capability. Never propose calendar events or blocked time. If something feels like it belongs on a calendar (a workout, a call, a deadline), turn it into a TASK with a due date instead — the Tasks board already handles scheduling fine.

Tasks can recur. If a task is a recurring habit or routine rather than a one-off action (daily journaling, a weekly review, a monthly check-in), give it a "recurrence" of "daily", "weekly", "monthly", or "annual" instead of proposing it as separate one-off tasks. Use null for ordinary one-off tasks — most tasks should be null.`;
  }

  async function send(question) {
    if (!question.trim() || loading) return;
    setLoading(true);
    setMessages(m => [...m, { role: "user", text: question }]);
    setInput("");
    try {
      const history = messages.map(m => `${m.role === "user" ? "Sean" : "Coach"}: ${m.text}`).join("\n");
      const prompt = `${buildContext()}\n\nConversation so far:\n${history}\nSean: ${question}\n\nRespond as the Coach — warm, energetic, a few sentences. Ask one good clarifying question if you need more to go on, don't interrogate. This is a chat reply, not structured output — plain text only.`;
      const text = await callWorker([{ role: "user", content: prompt }], 500);
      setMessages(m => [...m, { role: "assistant", text: text || "Didn't get anything back there — try again?" }]);
    } catch (err) {
      setMessages(m => [...m, { role: "assistant", text: "Something went wrong reaching the coach: " + err.message }]);
    } finally {
      setLoading(false);
    }
  }

  async function buildPlan() {
    setLoading(true);
    setMessages(m => [...m, { role: "user", text: "Let's build the plan." }]);
    try {
      const history = messages.map(m => `${m.role === "user" ? "Sean" : "Coach"}: ${m.text}`).join("\n");
      const prompt = `${buildContext()}\n\nConversation so far:\n${history}\n\nBased on everything above, propose a concrete plan. Decide whether this goal is better tracked as "manual" (a plain number, like books read) or "tasks" (progress comes from checking off linked tasks — better for project-shaped goals). Break it into 2-4 phases with real target dates between now and the deadline. For phase 1 ONLY, propose 2-5 concrete, specific tasks with due dates in the next few weeks — don't generate tasks for later phases yet, those get built when Sean actually gets there.

Respond with ONLY a JSON object, no prose, no markdown fences, in exactly this shape:
{"title":"...","progressMode":"manual"|"tasks","target":number|null,"unit":string|null,"dueDate":"YYYY-MM-DD","lens":"work"|"gmu"|"home"|"growth","phases":[{"title":"...","dueDate":"YYYY-MM-DD"}],"tasks":[{"title":"...","dueDate":"YYYY-MM-DD","phaseIndex":0,"recurrence":null}]}`;
      const text = await callWorker([{ role: "user", content: prompt }], 1500);
      const parsed = extractJSON(text);
      if (!parsed) throw new Error("couldn't parse a plan from the response");
      setMessages(m => [...m, { role: "assistant", text: "Here's the plan, take a look below. Nothing's saved yet, review it and hit Apply if it feels right." }]);
      setProposedPlan(parsed);
    } catch (err) {
      setMessages(m => [...m, { role: "assistant", text: "Something went wrong building the plan: " + err.message }]);
    } finally {
      setLoading(false);
    }
  }

  // Scoped addition to an EXISTING goal — one new phase (with its own first
  // tasks), or a batch of tasks for a phase that already exists. Kept
  // separate from buildPlan on purpose: asking the model to reproduce
  // everything else about the goal untouched, alongside one small change,
  // is more failure-prone than just asking for the small change itself.
  async function buildDelta() {
    setLoading(true);
    const actionLabel = mode === "add-phase" ? "Let's add a new phase." : mode === "add-tasks" ? "Let's add tasks to an existing phase." : "Let's break this task down.";
    setMessages(m => [...m, { role: "user", text: actionLabel }]);
    try {
      const history = messages.map(m => `${m.role === "user" ? "Sean" : "Coach"}: ${m.text}`).join("\n");
      let prompt;
      if (mode === "add-phase") {
        prompt = `${buildContext()}\n\nConversation so far:\n${history}\n\nPropose ONE new phase that comes next for this goal, with 2-5 concrete tasks for it, due within the next few weeks.\n\nRespond with ONLY a JSON object, no prose, no markdown fences, in exactly this shape:\n{"title":"...","dueDate":"YYYY-MM-DD","tasks":[{"title":"...","dueDate":"YYYY-MM-DD","recurrence":null}]}`;
      } else if (mode === "add-tasks") {
        const targetPhase = goal.phases.find(p => p.id === selectedPhaseId);
        prompt = `${buildContext()}\n\nConversation so far:\n${history}\n\nPropose 2-5 concrete tasks for the existing phase "${targetPhase?.title}", due within the next few weeks.\n\nRespond with ONLY a JSON object, no prose, no markdown fences, in exactly this shape:\n{"tasks":[{"title":"...","dueDate":"YYYY-MM-DD","recurrence":null}]}`;
      } else {
        const targetTask = (tasks || []).find(t => t.id === Number(selectedTaskId) || t.id === selectedTaskId);
        prompt = `${buildContext()}\n\nConversation so far:\n${history}\n\nBreak the task "${targetTask?.title}"${targetTask?.dueDate ? ` (currently due ${targetTask.dueDate})` : ""} into 2-4 smaller, concrete sub-tasks that together accomplish it. Spread their due dates sensibly leading up to${targetTask?.dueDate ? ` ${targetTask.dueDate}` : " the original deadline"}.\n\nRespond with ONLY a JSON object, no prose, no markdown fences, in exactly this shape:\n{"subtasks":[{"title":"...","dueDate":"YYYY-MM-DD","recurrence":null}]}`;
      }
      const text = await callWorker([{ role: "user", content: prompt }], 1000);
      const parsed = extractJSON(text);
      if (!parsed) throw new Error("couldn't parse a result from the response");
      setMessages(m => [...m, { role: "assistant", text: mode === "add-phase" ? "Here's the next phase, take a look below." : mode === "add-tasks" ? "Here are some tasks for that phase, take a look below." : "Here's the breakdown, take a look below." }]);
      if (mode === "add-phase") setProposedDelta({ kind: mode, payload: parsed });
      else if (mode === "add-tasks") setProposedDelta({ kind: mode, payload: { ...parsed, phaseId: selectedPhaseId } });
      else setProposedDelta({ kind: "split-task", payload: { ...parsed, originalTaskId: (tasks || []).find(t => t.id === Number(selectedTaskId) || t.id === selectedTaskId)?.id } });
    } catch (err) {
      setMessages(m => [...m, { role: "assistant", text: "Something went wrong: " + err.message }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ ...panel, borderColor: tint(accent, 27), borderLeft: `3px solid ${accent}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ ...monoLabel, color: accentText, marginBottom: 0 }}>🎯 Goals Coach</div>
        <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-faint)", fontSize: 14 }}>✕</button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: isMobile ? 220 : 280, overflowY: "auto", marginBottom: 10, paddingRight: 4 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%", background: m.role === "user" ? tint(accent, 13) : "var(--panel-soft)", border: `1px solid ${m.role === "user" ? tint(accent, 27) : "var(--border)"}`, borderRadius: 8, padding: isMobile ? "9px 12px" : "8px 11px", fontSize: isMobile ? 13 : 12, color: "var(--ink)", lineHeight: 1.5 }}>
            {m.text}
          </div>
        ))}
        {loading && <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>Thinking…</div>}
        <div ref={messagesEndRef} />
      </div>

      {proposedPlan ? (
        <div style={{ background: "var(--panel-soft)", border: `1px solid ${tint(accent, 40)}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>{proposedPlan.title}</div>
          <div style={{ fontSize: 11, color: "var(--ink-soft)", marginBottom: 8 }}>
            {proposedPlan.progressMode === "tasks" ? "Tracked by linked tasks" : `${proposedPlan.target ?? "?"} ${proposedPlan.unit || ""}`}
            {proposedPlan.dueDate && ` · Due ${proposedPlan.dueDate}`}
          </div>
          {proposedPlan.phases?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {proposedPlan.phases.map((p, i) => (
                <div key={i} style={{ fontSize: 11, color: "var(--ink-mid)", marginBottom: 2 }}>Phase {i + 1}: {p.title}{p.dueDate ? ` (by ${p.dueDate})` : ""}</div>
              ))}
            </div>
          )}
          {proposedPlan.tasks?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>First tasks</div>
              {proposedPlan.tasks.map((t, i) => (
                <div key={i} style={{ fontSize: 11, color: "var(--ink-mid)", marginBottom: 2 }}>• {t.title}{t.dueDate ? ` — due ${t.dueDate}` : ""}{t.recurrence && <span style={{ color: "var(--ink-faint)" }}> · ↺ {RECURRENCE_LABELS[t.recurrence]}</span>}</div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => onApply(proposedPlan)} style={{ padding: isMobile ? "9px 16px" : "6px 14px", background: accent, border: "none", borderRadius: 6, color: "var(--on-accent)", cursor: "pointer", fontSize: isMobile ? 13 : 12, fontWeight: 600 }}>Apply</button>
            <button onClick={() => setProposedPlan(null)} style={{ padding: isMobile ? "9px 16px" : "6px 14px", background: "transparent", border: "1px solid var(--border)", borderRadius: 6, color: "var(--ink-soft)", cursor: "pointer", fontSize: isMobile ? 13 : 12 }}>Keep talking</button>
          </div>
        </div>
      ) : proposedDelta ? (
        <div style={{ background: "var(--panel-soft)", border: `1px solid ${tint(accent, 40)}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
          {proposedDelta.kind === "add-phase" && (
            <>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>New phase: {proposedDelta.payload.title}</div>
              {proposedDelta.payload.dueDate && <div style={{ fontSize: 11, color: "var(--ink-soft)", marginBottom: 8 }}>Target: {proposedDelta.payload.dueDate}</div>}
            </>
          )}
          {proposedDelta.kind === "add-tasks" && (
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>New tasks for "{goal.phases.find(p => p.id === proposedDelta.payload.phaseId)?.title}"</div>
          )}
          {proposedDelta.kind === "split-task" && (
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>
              Breaking down: "{(tasks || []).find(t => t.id === proposedDelta.payload.originalTaskId)?.title}"
            </div>
          )}
          {proposedDelta.kind === "split-task" ? (
            proposedDelta.payload.subtasks?.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Becomes</div>
                {proposedDelta.payload.subtasks.map((t, i) => (
                  <div key={i} style={{ fontSize: 11, color: "var(--ink-mid)", marginBottom: 2 }}>• {t.title}{t.dueDate ? ` — due ${t.dueDate}` : ""}{t.recurrence && <span style={{ color: "var(--ink-faint)" }}> · ↺ {RECURRENCE_LABELS[t.recurrence]}</span>}</div>
                ))}
              </div>
            )
          ) : (
            proposedDelta.payload.tasks?.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Tasks</div>
                {proposedDelta.payload.tasks.map((t, i) => (
                  <div key={i} style={{ fontSize: 11, color: "var(--ink-mid)", marginBottom: 2 }}>• {t.title}{t.dueDate ? ` — due ${t.dueDate}` : ""}{t.recurrence && <span style={{ color: "var(--ink-faint)" }}> · ↺ {RECURRENCE_LABELS[t.recurrence]}</span>}</div>
                ))}
              </div>
            )
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { onApplyDelta(proposedDelta.kind, proposedDelta.payload); setProposedDelta(null); setMode("chat"); setSelectedPhaseId(""); setSelectedTaskId(""); }} style={{ padding: isMobile ? "9px 16px" : "6px 14px", background: accent, border: "none", borderRadius: 6, color: "var(--on-accent)", cursor: "pointer", fontSize: isMobile ? 13 : 12, fontWeight: 600 }}>
              {proposedDelta.kind === "split-task" ? "Replace task" : "Add to goal"}
            </button>
            <button onClick={() => setProposedDelta(null)} style={{ padding: isMobile ? "9px 16px" : "6px 14px", background: "transparent", border: "1px solid var(--border)", borderRadius: 6, color: "var(--ink-soft)", cursor: "pointer", fontSize: isMobile ? 13 : 12 }}>Keep talking</button>
          </div>
        </div>
      ) : (
        <>
          {!isNew && (
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button onClick={() => setMode(mode === "add-phase" ? "chat" : "add-phase")} style={{ fontSize: isMobile ? 12 : 11, padding: isMobile ? "7px 11px" : "5px 10px", borderRadius: 5, border: `1px solid ${mode === "add-phase" ? accent : "var(--border)"}`, background: mode === "add-phase" ? tint(accent, 13) : "transparent", color: mode === "add-phase" ? accentText : "var(--ink-soft)", cursor: "pointer" }}>➕ Add a phase</button>
              {goal.phases?.length > 0 && (
                <select
                  value={mode === "add-tasks" ? selectedPhaseId : ""}
                  onChange={e => { setSelectedPhaseId(e.target.value); setMode(e.target.value ? "add-tasks" : "chat"); }}
                  style={{ fontSize: isMobile ? 12 : 11, padding: isMobile ? "7px 9px" : "5px 8px", borderRadius: 5, border: `1px solid ${mode === "add-tasks" ? accent : "var(--border)"}`, background: mode === "add-tasks" ? tint(accent, 13) : "var(--panel-soft)", color: mode === "add-tasks" ? accentText : "var(--ink-soft)" }}
                >
                  <option value="">➕ Add tasks to a phase…</option>
                  {goal.phases.map((p, i) => <option key={p.id} value={p.id}>{`Phase ${i + 1}: ${p.title}`}</option>)}
                </select>
              )}
              {(tasks || []).filter(t => t.goalId === goal.id).length > 0 && (
                <select
                  value={mode === "split-task" ? selectedTaskId : ""}
                  onChange={e => { setSelectedTaskId(e.target.value); setMode(e.target.value ? "split-task" : "chat"); }}
                  style={{ fontSize: isMobile ? 12 : 11, padding: isMobile ? "7px 9px" : "5px 8px", borderRadius: 5, border: `1px solid ${mode === "split-task" ? accent : "var(--border)"}`, background: mode === "split-task" ? tint(accent, 13) : "var(--panel-soft)", color: mode === "split-task" ? accentText : "var(--ink-soft)" }}
                >
                  <option value="">🔨 Break down a task…</option>
                  {(tasks || []).filter(t => t.goalId === goal.id).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              )}
            </div>
          )}
          {mode !== "chat" && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: accentText }}>
                {mode === "add-phase" ? "Describe the next phase, then propose it below"
                  : mode === "add-tasks" ? "Describe what's needed, then propose tasks below"
                  : "Add any context, then propose the breakdown below"}
              </span>
              <button onClick={() => { setMode("chat"); setSelectedPhaseId(""); setSelectedTaskId(""); }} style={{ fontSize: 11, color: "var(--ink-faint)", background: "transparent", border: "none", cursor: "pointer" }}>Cancel</button>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send(input)}
              placeholder="Tell the coach what's on your mind…"
              disabled={loading}
              style={{ flex: 1, background: "var(--bg)", border: "1px solid var(--border-soft)", borderRadius: 6, padding: isMobile ? "10px 12px" : "8px 11px", color: "var(--ink)", fontSize: isMobile ? 14 : 12, outline: "none", fontFamily: "'DM Sans',sans-serif" }}
            />
            <button onClick={() => send(input)} disabled={loading} style={{ padding: isMobile ? "10px 16px" : "8px 14px", background: accent, border: "none", borderRadius: 6, color: "var(--on-accent)", cursor: "pointer", fontSize: isMobile ? 13 : 12, fontWeight: 600 }}>Send</button>
          </div>
          <button
            onClick={mode === "chat" ? buildPlan : buildDelta}
            disabled={loading || (mode === "add-tasks" && !selectedPhaseId) || (mode === "split-task" && !selectedTaskId)}
            style={{ width: "100%", padding: isMobile ? 11 : 8, background: "transparent", border: `1px solid ${tint(accent, 40)}`, borderRadius: 6, color: accentText, cursor: "pointer", fontSize: isMobile ? 13 : 12, fontWeight: 600 }}
          >
            {mode === "chat" ? "✦ Ready — build my plan" : mode === "add-phase" ? "✦ Propose this phase" : mode === "add-tasks" ? "✦ Propose these tasks" : "✦ Propose the breakdown"}
          </button>
        </>
      )}
    </div>
  );
}

// ─── QUICK CAPTURE ────────────────────────────────────────────────────────────
// Backed by a dedicated, permanent list inside `lists` (id QUICK_CAPTURE_LIST_ID)
// rather than its own separate synced array — a quick capture is really just
// a list item with nowhere else to live yet. `protected: true` keeps it out of
// the normal "Delete list" flow; `noGroups: true` keeps it flat, matching
// Sean's call that this one specific list shouldn't have sub-groupings.
// Dismissing here is a true delete (not a check-off) — same as before.
export const QUICK_CAPTURE_LIST_ID = "quick-capture-list";

export function QuickCapture({ lists, onListsChange }) {
  const [note, setNote] = useState("");
  const allLists = lists || [];
  const qcList = allLists.find(l => l.id === QUICK_CAPTURE_LIST_ID);
  const items = qcList?.items || [];
  const recent = items.slice(-8).reverse();

  function capture() {
    if (!note.trim()) return;
    const item = { id: Date.now(), text: note.trim(), done: false, groupId: null, ts: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) };
    const updated = qcList
      ? allLists.map(l => l.id === QUICK_CAPTURE_LIST_ID ? { ...l, items: [...l.items, item] } : l)
      : [...allLists, { id: QUICK_CAPTURE_LIST_ID, title: "Quick Capture", lens: "all", protected: true, noGroups: true, groups: [], items: [item] }];
    onListsChange(updated);
    setNote("");
  }
  function dismiss(id) {
    onListsChange(allLists.map(l => l.id === QUICK_CAPTURE_LIST_ID ? { ...l, items: l.items.filter(i => i.id !== id) } : l));
  }

  return (
    <div style={panel}>
      <div style={{...monoLabel}}>Quick Capture</div>
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <input value={note} onChange={e=>setNote(e.target.value)} onKeyDown={e=>e.key==="Enter"&&capture()} placeholder="Capture a task, thought, or idea…" style={{flex:1,background:"var(--panel-soft)",border:"1px solid var(--border)",borderRadius:7,padding:"9px 12px",color:"var(--ink)",fontSize:13,fontFamily:"'DM Sans',sans-serif",outline:"none"}}/>
        <button onClick={capture} style={{padding:"9px 16px",background:"var(--blue)",border:"none",borderRadius:7,color:"var(--on-accent)",cursor:"pointer",fontSize:13,fontWeight:600}}>+ Add</button>
      </div>
      {recent.length>0 && (
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {recent.map(c=>(
            <div key={c.id} className="slide-in" style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,color:"var(--ink-mid)",padding:"6px 10px",background:"var(--panel-soft)",borderRadius:6,border:"1px solid var(--border)"}}>
              <div>{c.text}</div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {c.ts && <span style={{fontSize:10,color:"var(--ink-faint)",fontFamily:"'DM Mono',monospace"}}>{c.ts}</span>}
                <button onClick={()=>dismiss(c.id)} style={{background:"transparent",border:"none",cursor:"pointer",color:"var(--ink-faint)",fontSize:12}}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── GREETING STRIP ───────────────────────────────────────────────────────────
export function GreetingStrip({ lens, events, tasks, goals }) {
  const lMeta = LENSES.find(l=>l.id===lens);
  const accent = lMeta?.accent ?? "var(--blue)";
  const hour = new Date().getHours();
  const greeting = hour<12?"Good morning":hour<17?"Good afternoon":"Good evening";
  const todayKey = toDateKey(new Date());
  const todayCount = (events||[]).filter(e=>e.dateKey===todayKey).length;
  const openTasks = byLens(tasks,lens).filter(t=>t.col!=="Done").length;
  const overdue = byLens(tasks,lens).filter(t=>t.col!=="Done"&&isOverdue(t.dueDate)).length;
  const activeGoals = byLens(goals,lens).filter(g=>!g.complete).length;

  return (
    <div style={{background:`linear-gradient(135deg,${tint(accent, 8)},transparent)`,border:`1px solid ${tint(accent, 13)}`,borderRadius:12,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
      <div>
        <div style={{fontSize:22,fontWeight:700,color:"var(--ink)",fontFamily:"'Playfair Display',serif"}}>{greeting}, Sean.</div>
        <div style={{fontSize:12,color:"var(--ink-mid)",marginTop:4}}>
          {lMeta?.label} · {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})} · {todayCount} events today · {openTasks} open tasks · {activeGoals} goals
          {overdue>0&&<span style={{color:"var(--danger-badge-text)",marginLeft:6,fontWeight:600}}>· ⚠ {overdue} overdue</span>}
        </div>
      </div>
    </div>
  );
}

// ─── DINNER DATA (shared by DinnerWidget + DinnerView) ────────────────────────
// Both read the same two Worker endpoints MealPlanner.js uses (/meals,
// /meal-plan/:weekKey), via this one hook, so the compact widget and the full
// sidebar view can never disagree about what's on the board. Deliberately
// read-only — editing the board still happens on /meal-planner.
const MEAL_WORKER_URL = "https://anthropic-proxy-worker.seancprince.workers.dev";
const PLAN_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function mondayOfThisWeek() {
  const d = new Date();
  const day = d.getDay();
  const diffToMon = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMon);
  return d;
}

function todayPlanKey() {
  const day = new Date().getDay();
  return PLAN_DAYS[day === 0 ? 6 : day - 1];
}

function dateForDayIndex(i) {
  const d = mondayOfThisWeek();
  d.setDate(d.getDate() + i);
  return d;
}

function useMealPlanData() {
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [meals, setMeals] = useState([]);
  const [plan, setPlan] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const weekKey = mondayOfThisWeek().toISOString().slice(0, 10);
        const [mealsRes, planRes] = await Promise.all([
          fetch(`${MEAL_WORKER_URL}/meals`),
          fetch(`${MEAL_WORKER_URL}/meal-plan/${weekKey}`),
        ]);
        const mealsData = mealsRes.ok ? await mealsRes.json() : [];
        const weekData = planRes.ok ? await planRes.json() : null;
        if (!cancelled) {
          setMeals(mealsData);
          setPlan(weekData?.plan || {});
          setStatus("ready");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { status, meals, plan };
}

// ─── DINNER WIDGET (compact, on the main dashboard) ───────────────────────────
export function DinnerWidget({ lens }) {
  const isMobile = useIsMobile();
  const accent = lensAccent(lens);
  const accentText = lensAccentText(lens);
  const { status, meals, plan } = useMealPlanData();
  const today = todayPlanKey();
  const nameFor = (id) => (id ? meals.find(m => m.id === id)?.name || null : null);
  const tonight = { dinner: nameFor(plan[today]?.dinner), side: nameFor(plan[today]?.side) };

  return (
    <div style={panel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ ...monoLabel, marginBottom: 0 }}>Tonight's Dinner</div>
        <button
          onClick={() => window.open("/meal-planner", "_blank")}
          style={{ background: "transparent", border: `1px solid ${tint(accent, 27)}`, borderRadius: 6, color: accentText, fontSize: 11, padding: "4px 10px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
        >
          Open Meal Planner ↗
        </button>
      </div>

      {status === "loading" && <div style={{ fontSize: 12, color: "var(--ink-faint)" }}>Loading this week's board…</div>}
      {status === "error" && <div style={{ fontSize: 12, color: "var(--red-text)" }}>Couldn't reach the meal planner.</div>}

      {status === "ready" && (
        <>
          <div style={{ marginBottom: 14 }}>
            {tonight.dinner ? (
              <>
                <div style={{ fontSize: 20, fontWeight: 600, color: "var(--ink)", fontFamily: "'Playfair Display', serif" }}>{tonight.dinner}</div>
                {tonight.side && <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 2 }}>with {tonight.side}</div>}
              </>
            ) : (
              <div style={{ fontSize: 13, color: "var(--ink-faint)" }}>Nothing on the board for tonight yet.</div>
            )}
          </div>

          <div style={{ display: isMobile ? "flex" : "grid", gridTemplateColumns: isMobile ? undefined : "repeat(7, 1fr)", gap: 6, overflowX: isMobile ? "auto" : undefined, paddingBottom: isMobile ? 2 : undefined }}>
            {PLAN_DAYS.map(day => {
              const isToday = day === today;
              const dinnerName = nameFor(plan[day]?.dinner);
              return (
                <div
                  key={day}
                  title={dinnerName || ""}
                  style={{ background: isToday ? tint(accent, 9) : "var(--panel-soft)", border: `1px solid ${isToday ? tint(accent, 33) : "var(--border)"}`, borderRadius: 7, padding: "6px 4px", textAlign: "center", flexShrink: isMobile ? 0 : undefined, minWidth: isMobile ? 56 : undefined }}
                >
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: isToday ? accentText : "var(--ink-faint)", fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>
                    {day.toUpperCase()}
                  </div>
                  <div style={{ fontSize: 10, color: dinnerName ? "var(--ink-mid)" : "var(--ink-disabled)", lineHeight: 1.25, minHeight: 24, overflow: "hidden" }}>
                    {dinnerName ? trunc(dinnerName, 10) : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── DINNER VIEW (full sidebar destination: expanded card + expanded week) ────
export function DinnerView({ lens }) {
  const isMobile = useIsMobile();
  const accent = lensAccent(lens);
  const accentText = lensAccentText(lens);
  const { status, meals, plan } = useMealPlanData();
  const today = todayPlanKey();
  const findMeal = (id) => (id ? meals.find(m => m.id === id) : null);

  const tonightSlot = plan[today] || {};
  const tonightDinner = findMeal(tonightSlot.dinner);
  const tonightSide = findMeal(tonightSlot.side);
  const tonightTags = [tonightDinner?.attributes?.cuisine, tonightDinner?.attributes?.protein, tonightDinner?.attributes?.course].filter(Boolean);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {status === "loading" && <div style={panel}><div style={{ fontSize: 13, color: "var(--ink-faint)" }}>Loading this week's board…</div></div>}
      {status === "error" && <div style={panel}><div style={{ fontSize: 13, color: "var(--red-text)" }}>Couldn't reach the meal planner — check the Worker connection.</div></div>}

      {status === "ready" && (
        <>
          {/* Big tonight card */}
          <div style={{ ...panel, borderLeft: `3px solid ${accent}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div style={{ ...monoLabel, marginBottom: 0 }}>
                Tonight · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </div>
              <button
                onClick={() => window.open("/meal-planner", "_blank")}
                style={{ background: "transparent", border: `1px solid ${tint(accent, 27)}`, borderRadius: 6, color: accentText, fontSize: 12, padding: "5px 12px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", flexShrink: 0 }}
              >
                Open Meal Planner ↗
              </button>
            </div>

            {tonightDinner ? (
              <>
                <div style={{ fontSize: 30, fontWeight: 700, color: "var(--ink)", fontFamily: "'Playfair Display', serif", marginBottom: 4, lineHeight: 1.15 }}>
                  {tonightDinner.name}
                </div>
                {tonightSide && <div style={{ fontSize: 15, color: "var(--ink-mid)", marginBottom: 12 }}>with {tonightSide.name}</div>}
                {tonightDinner.notes && (
                  <div style={{ fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.55, marginBottom: 12, maxWidth: 640 }}>{tonightDinner.notes}</div>
                )}
                {tonightTags.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {tonightTags.map(tag => (
                      <span key={tag} style={{ fontSize: 10, padding: "3px 9px", borderRadius: 12, background: tint(accent, 9), color: accentText, fontFamily: "'DM Mono', monospace" }}>{tag}</span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 14, color: "var(--ink-faint)" }}>Nothing on the board for tonight yet — add one on the full Meal Planner.</div>
            )}
          </div>

          {/* Expanded week board */}
          <div style={panel}>
            <div style={monoLabel}>This Week's Dinner Board</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(7, 1fr)", gap: 10 }}>
              {PLAN_DAYS.map((day, i) => {
                const isToday = day === today;
                const slot = plan[day] || {};
                const dinner = findMeal(slot.dinner);
                const side = findMeal(slot.side);
                const date = dateForDayIndex(i);
                return (
                  <div
                    key={day}
                    style={{ background: isToday ? tint(accent, 8) : "var(--panel-soft)", border: `1px solid ${isToday ? tint(accent, 33) : "var(--border)"}`, borderRadius: 9, padding: "12px 10px", minHeight: 118, display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: isToday ? accentText : "var(--ink-soft)", fontFamily: "'DM Mono', monospace" }}>{day.toUpperCase()}</div>
                      <div style={{ fontSize: 10, color: "var(--ink-faint)", fontFamily: "'DM Mono', monospace" }}>{date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                    </div>
                    <div style={{ fontSize: 12.5, color: dinner ? "var(--ink)" : "var(--ink-disabled)", fontWeight: 500, lineHeight: 1.3 }}>{dinner?.name || "—"}</div>
                    {side && <div style={{ fontSize: 11, color: "var(--ink-dim)" }}>+ {side.name}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── LISTS ──────────────────────────────────────────────────────────────────
// Freeform shared lists (Xmas ideas, vacation ideas, Thanksgiving planning,
// etc.) — one lens per list (no per-item/per-group lens), one level of
// grouping within a list, items optionally carry a link. State lives in
// App.js and syncs the same way tasks/goals/quick-capture do.
function ConfirmDeleteListModal({ list, onConfirm, onCancel }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ position:"fixed", inset:0, background:"var(--overlay)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
      <div style={{ background:"var(--panel-soft)", border:"1px solid var(--border)", borderRadius:10, padding:20, maxWidth:380, width:"100%" }}>
        <div style={{ fontSize:isMobile?15:14, fontWeight:600, color:"var(--ink)", marginBottom:10 }}>Delete "{list.title}"?</div>
        <div style={{ fontSize:isMobile?13:12, color:"var(--ink-soft)", marginBottom:16 }}>This deletes all {list.items.length} item{list.items.length===1?"":"s"} on it. This can't be undone.</div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onConfirm} style={{ flex:1, padding:isMobile?11:8, background:"var(--red)", border:"none", borderRadius:6, color:"var(--on-accent)", cursor:"pointer", fontSize:isMobile?13:12, fontWeight:600 }}>Delete</button>
          <button onClick={onCancel} style={{ flex:1, padding:isMobile?11:8, background:"transparent", border:"1px solid var(--border)", borderRadius:6, color:"var(--ink-soft)", cursor:"pointer", fontSize:isMobile?13:12 }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Deleting a group never silently loses its items — same "cascade or keep"
// choice pattern already used for goal/phase deletion, mirrored here rather
// than sharing that component directly (different nouns, different data).
function ConfirmDeleteGroupModal({ group, itemCount, onKeepItems, onDeleteItems, onCancel }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ position:"fixed", inset:0, background:"var(--overlay)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }}>
      <div style={{ background:"var(--panel-soft)", border:"1px solid var(--border)", borderRadius:10, padding:20, maxWidth:380, width:"100%" }}>
        <div style={{ fontSize:isMobile?15:14, fontWeight:600, color:"var(--ink)", marginBottom:10 }}>Remove "{group.title}"?</div>
        {itemCount>0 ? (
          <>
            <div style={{ fontSize:isMobile?13:12, color:"var(--ink-soft)", marginBottom:16, lineHeight:1.6 }}>
              This group has {itemCount} item{itemCount===1?"":"s"}. What do you want to do with {itemCount===1?"it":"them"}?
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <button onClick={onKeepItems} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:"1px solid var(--border)", borderRadius:6, color:"var(--ink)", cursor:"pointer", fontSize:isMobile?13:12, textAlign:"left" }}>
                Remove this group only — move {itemCount===1?"it":"them"} to the main list
              </button>
              <button onClick={onDeleteItems} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:`1px solid ${tint("var(--red)", 40)}`, borderRadius:6, color:"var(--red-text)", cursor:"pointer", fontSize:isMobile?13:12, textAlign:"left" }}>
                Remove this group and its {itemCount} item{itemCount===1?"":"s"}
              </button>
              <button onClick={onCancel} style={{ padding:isMobile?"11px 14px":"9px 12px", background:"transparent", border:"none", borderRadius:6, color:"var(--ink-faint)", cursor:"pointer", fontSize:isMobile?13:12 }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize:isMobile?13:12, color:"var(--ink-soft)", marginBottom:16 }}>This group is empty.</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={onDeleteItems} style={{ flex:1, padding:isMobile?11:8, background:"var(--red)", border:"none", borderRadius:6, color:"var(--on-accent)", cursor:"pointer", fontSize:isMobile?13:12, fontWeight:600 }}>Remove</button>
              <button onClick={onCancel} style={{ flex:1, padding:isMobile?11:8, background:"transparent", border:"1px solid var(--border)", borderRadius:6, color:"var(--ink-soft)", cursor:"pointer", fontSize:isMobile?13:12 }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ListItemRow({ item, accent, onToggle, onDelete, otherLists, moving, onMoveOpen, onMoveTo, onMoveCancel }) {
  const isMobile = useIsMobile();
  return (
    <div style={{ display:"flex", alignItems:"center", gap:isMobile?10:8, padding:isMobile?"8px 4px":"6px 4px", borderBottom:"1px solid var(--panel-alt)", flexWrap:"wrap" }}>
      <input type="checkbox" checked={item.done} onChange={onToggle} style={{ accentColor:accent, cursor:"pointer", flexShrink:0, width:isMobile?18:14, height:isMobile?18:14 }}/>
      <span style={{ flex:1, fontSize:isMobile?13:12, color:item.done?"var(--ink-faint)":"var(--ink)", textDecoration:item.done?"line-through":"none", wordBreak:"break-word" }}>{item.text}</span>
      {moving ? (
        <select autoFocus defaultValue="" onChange={e=>e.target.value&&onMoveTo(e.target.value)} onBlur={onMoveCancel} style={{ fontSize:11, background:"var(--bg)", border:"1px solid var(--border-soft)", borderRadius:4, color:"var(--ink-mid)", padding:"3px 6px" }}>
          <option value="" disabled>Move to…</option>
          {otherLists.map(l=><option key={l.id} value={l.id}>{l.title}</option>)}
        </select>
      ) : (
        otherLists.length > 0 && <button onClick={onMoveOpen} title="Move to another list" style={{ background:"transparent", border:"none", cursor:"pointer", color:"var(--ink-faint)", fontSize:isMobile?14:11, padding:isMobile?4:0, flexShrink:0 }}>→</button>
      )}
      <button onClick={onDelete} style={{ background:"transparent", border:"none", cursor:"pointer", color:"var(--ink-faint)", fontSize:isMobile?14:11, padding:isMobile?4:0, flexShrink:0 }}>✕</button>
    </div>
  );
}

export function ListsView({ lens, lists, onListsChange }) {
  const isMobile = useIsMobile();
  const [adding, setAdding] = useState(false);
  const [newListTitle, setNewListTitle] = useState("");
  const [newListLens, setNewListLens] = useState(lens==="all"?"work":lens);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  // { listId, groupId } — groupId null means the list's ungrouped section.
  // Which group the form targets is implicit from where "+ Add item" was
  // clicked, so there's no group-picker in the form itself.
  const [addingItemTo, setAddingItemTo] = useState(null);
  const [itemText, setItemText] = useState("");
  const [addingGroupTo, setAddingGroupTo] = useState(null);
  const [groupTitle, setGroupTitle] = useState("");
  const [deleteListConfirm, setDeleteListConfirm] = useState(null);
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState(null);
  const [movingItem, setMovingItem] = useState(null); // itemId or null
  const accent = lensAccent(lens);
  const allLists = lists || [];
  // The Quick Capture list is always visible regardless of the active lens
  // tab — it's a cross-cutting inbox, not scoped to one lens like a normal list.
  const visibleLists = allLists.filter(l => l.id === QUICK_CAPTURE_LIST_ID || lens === "all" || l.lens === lens);

  function addList() {
    if (!newListTitle.trim()) return;
    onListsChange([...allLists, { id: Date.now(), title: newListTitle.trim(), lens: newListLens, groups: [], items: [] }]);
    setNewListTitle(""); setAdding(false);
  }
  function deleteList(id) {
    onListsChange(allLists.filter(l => l.id !== id));
    setDeleteListConfirm(null);
  }
  function addGroup(listId) {
    if (!groupTitle.trim()) return;
    const groupId = `group-${Date.now()}`;
    onListsChange(allLists.map(l => l.id === listId ? { ...l, groups: [...l.groups, { id: groupId, title: groupTitle.trim() }] } : l));
    setGroupTitle(""); setAddingGroupTo(null);
  }
  function deleteGroup(listId, groupId, alsoDeleteItems) {
    onListsChange(allLists.map(l => {
      if (l.id !== listId) return l;
      const groups = l.groups.filter(g => g.id !== groupId);
      const items = alsoDeleteItems ? l.items.filter(i => i.groupId !== groupId) : l.items.map(i => i.groupId === groupId ? { ...i, groupId: null } : i);
      return { ...l, groups, items };
    }));
    setDeleteGroupConfirm(null);
  }
  function addItem(listId, groupId) {
    if (!itemText.trim()) return;
    const item = { id: Date.now(), text: itemText.trim(), done: false, groupId: groupId || null };
    onListsChange(allLists.map(l => l.id === listId ? { ...l, items: [...l.items, item] } : l));
    setItemText(""); setAddingItemTo(null);
  }
  function toggleItem(listId, itemId) {
    onListsChange(allLists.map(l => l.id === listId ? { ...l, items: l.items.map(i => i.id === itemId ? { ...i, done: !i.done } : i) } : l));
  }
  function deleteItem(listId, itemId) {
    onListsChange(allLists.map(l => l.id === listId ? { ...l, items: l.items.filter(i => i.id !== itemId) } : l));
  }
  function moveItem(fromListId, itemId, toListId) {
    // toListId comes from a <select> value, which is always a string, while
    // list ids can be numbers (Date.now()) — compare as strings so a numeric
    // id still matches instead of silently never hitting the toListId branch.
    const toId = String(toListId);
    const fromList = allLists.find(l => l.id === fromListId);
    const item = fromList?.items.find(i => i.id === itemId);
    if (!item) return;
    onListsChange(allLists.map(l => {
      if (l.id === fromListId) return { ...l, items: l.items.filter(i => i.id !== itemId) };
      if (String(l.id) === toId) return { ...l, items: [...l.items, { ...item, groupId: null }] };
      return l;
    }));
    setMovingItem(null);
  }
  function toggleCollapse(groupId) {
    setCollapsedGroups(prev => { const next = new Set(prev); next.has(groupId) ? next.delete(groupId) : next.add(groupId); return next; });
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={panel}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: adding ? 14 : 0 }}>
          <div style={{ ...monoLabel, marginBottom:0 }}>Lists</div>
          <button onClick={() => setAdding(a => !a)} style={{ padding:"5px 12px", background: adding ? "var(--panel-alt)" : accent, border:"none", borderRadius:6, color: adding ? "var(--ink-soft)" : "var(--on-accent)", cursor:"pointer", fontSize:11, fontWeight:600 }}>{adding ? "Cancel" : "+ New List"}</button>
        </div>
        {adding && (
          <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap:8, marginTop:4 }}>
            <input autoFocus value={newListTitle} onChange={e=>setNewListTitle(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addList()} placeholder="List title (e.g. Xmas Ideas)" style={{ flex:2, background:"var(--bg)", border:"1px solid var(--border-soft)", borderRadius:5, padding:"7px 10px", color:"var(--ink)", fontSize:12, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
            <select value={newListLens} onChange={e=>setNewListLens(e.target.value)} style={{ flex:1, background:"var(--bg)", border:"1px solid var(--border-soft)", borderRadius:5, padding:"7px 10px", color:"var(--ink-mid)", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
              {LENSES.filter(l=>l.id!=="all").map(l=><option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
            <button onClick={addList} style={{ padding:"7px 14px", background:accent, border:"none", borderRadius:5, color:"var(--on-accent)", cursor:"pointer", fontSize:12, fontWeight:600 }}>Save</button>
          </div>
        )}
      </div>

      {visibleLists.length === 0 ? (
        <div style={panel}><div style={{ fontSize:13, color:"var(--ink-faint)", padding:"20px 0", textAlign:"center" }}>No lists yet for this lens. Create one above.</div></div>
      ) : visibleLists.map(list => {
        const listAccent = lensAccent(list.lens);
        const ungrouped = list.items.filter(i => !i.groupId);
        const otherLists = allLists.filter(l => l.id !== list.id);
        const itemRowProps = (item) => ({
          key: item.id, item, accent: listAccent,
          onToggle: () => toggleItem(list.id, item.id),
          onDelete: () => deleteItem(list.id, item.id),
          otherLists,
          moving: movingItem === item.id,
          onMoveOpen: () => setMovingItem(item.id),
          onMoveTo: (toListId) => moveItem(list.id, item.id, toListId),
          onMoveCancel: () => setMovingItem(null),
        });
        return (
          <div key={list.id} style={{ ...panel, borderLeft:`3px solid ${listAccent}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, gap:8, flexWrap:"wrap" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:14, fontWeight:600, color:"var(--ink)" }}>{list.title}</span>
                {!list.protected && <span style={{ fontSize:9, padding:"1px 5px", borderRadius:3, background:tint(listAccent, 13), color:listAccent, fontFamily:"'DM Mono',monospace" }}>{lensLabel(list.lens)}</span>}
              </div>
              {!list.protected && (
                <button onClick={() => setDeleteListConfirm(list)} title="Delete list" style={{ background:"transparent", border:"none", cursor:"pointer", color:"var(--ink-faint)", fontSize:isMobile?15:12, padding:isMobile?6:0 }}>✕</button>
              )}
            </div>

            {ungrouped.map(item => <ListItemRow {...itemRowProps(item)} />)}

            {addingItemTo?.listId === list.id && addingItemTo?.groupId === null ? (
              <div style={{ marginTop:6, marginBottom:8, display:"flex", gap:6 }}>
                <input autoFocus value={itemText} onChange={e=>setItemText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addItem(list.id,null)} placeholder="Item…" style={{ flex:1, background:"var(--bg)", border:"1px solid var(--border-soft)", borderRadius:5, padding:"6px 8px", color:"var(--ink)", fontSize:12, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                <button onClick={() => addItem(list.id,null)} style={{ padding:"0 12px", background:listAccent, border:"none", borderRadius:5, color:"var(--on-accent)", cursor:"pointer", fontSize:11, fontWeight:600 }}>Add</button>
                <button onClick={() => { setAddingItemTo(null); setItemText(""); }} style={{ padding:"0 10px", background:"var(--panel-alt)", border:"none", borderRadius:5, color:"var(--ink-soft)", cursor:"pointer", fontSize:11 }}>Cancel</button>
              </div>
            ) : (
              <button onClick={() => setAddingItemTo({ listId:list.id, groupId:null })} style={{ width:"100%", marginTop: ungrouped.length?6:0, marginBottom:8, padding:isMobile?"9px":"6px", border:"1px dashed var(--border-soft)", borderRadius:5, background:"transparent", cursor:"pointer", color:"var(--ink-faint)", fontSize:11 }}>+ Add item</button>
            )}

            {list.groups.map(group => {
              const groupItems = list.items.filter(i => i.groupId === group.id);
              const collapsed = collapsedGroups.has(group.id);
              const isAddingHere = addingItemTo?.listId === list.id && addingItemTo?.groupId === group.id;
              return (
                <div key={group.id} style={{ marginTop:8, paddingTop:8, borderTop:"1px solid var(--panel-alt)" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer" }} onClick={() => toggleCollapse(group.id)}>
                    <span style={{ fontSize:11, fontWeight:600, color:"var(--ink-soft)" }}>
                      {collapsed ? "▸" : "▾"} {group.title}
                      {groupItems.length > 0 && <span style={{ color:"var(--ink-faint)", fontWeight:400 }}> ({groupItems.filter(i=>i.done).length}/{groupItems.length})</span>}
                    </span>
                    <button onClick={(e) => { e.stopPropagation(); setDeleteGroupConfirm({ listId:list.id, group, itemCount:groupItems.length }); }} title="Remove group" style={{ background:"transparent", border:"none", cursor:"pointer", color:"var(--ink-faint)", fontSize:isMobile?14:11, padding:isMobile?6:0 }}>✕</button>
                  </div>
                  {!collapsed && (
                    <div style={{ marginTop:6, marginLeft:isMobile?0:12 }}>
                      {isAddingHere ? (
                        <div style={{ marginBottom:6, display:"flex", gap:6 }}>
                          <input autoFocus value={itemText} onChange={e=>setItemText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addItem(list.id,group.id)} placeholder="Item…" style={{ flex:1, background:"var(--bg)", border:"1px solid var(--border-soft)", borderRadius:5, padding:"6px 8px", color:"var(--ink)", fontSize:12, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                          <button onClick={() => addItem(list.id,group.id)} style={{ padding:"0 12px", background:listAccent, border:"none", borderRadius:5, color:"var(--on-accent)", cursor:"pointer", fontSize:11, fontWeight:600 }}>Add</button>
                          <button onClick={() => { setAddingItemTo(null); setItemText(""); }} style={{ padding:"0 10px", background:"var(--panel-alt)", border:"none", borderRadius:5, color:"var(--ink-soft)", cursor:"pointer", fontSize:11 }}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setAddingItemTo({ listId:list.id, groupId:group.id })} style={{ width:"100%", marginBottom:6, padding:isMobile?"8px":"5px", border:"1px dashed var(--border-soft)", borderRadius:5, background:"transparent", cursor:"pointer", color:"var(--ink-faint)", fontSize:10 }}>+ Add item</button>
                      )}
                      {groupItems.map(item => <ListItemRow {...itemRowProps(item)} />)}
                    </div>
                  )}
                </div>
              );
            })}

            {!list.noGroups && (
              addingGroupTo === list.id ? (
                <div style={{ display:"flex", gap:6, marginTop:10 }}>
                  <input autoFocus value={groupTitle} onChange={e=>setGroupTitle(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addGroup(list.id)} placeholder="Group name…" style={{ flex:1, background:"var(--bg)", border:"1px solid var(--border-soft)", borderRadius:5, padding:"6px 8px", color:"var(--ink)", fontSize:11, outline:"none", fontFamily:"'DM Sans',sans-serif" }}/>
                  <button onClick={() => addGroup(list.id)} style={{ padding:"0 10px", background:listAccent, border:"none", borderRadius:5, color:"var(--on-accent)", cursor:"pointer", fontSize:11, fontWeight:600 }}>Add</button>
                  <button onClick={() => { setAddingGroupTo(null); setGroupTitle(""); }} style={{ padding:"0 10px", background:"var(--panel-alt)", border:"none", borderRadius:5, color:"var(--ink-soft)", cursor:"pointer", fontSize:11 }}>Cancel</button>
                </div>
              ) : (
                <button onClick={() => setAddingGroupTo(list.id)} style={{ width:"100%", marginTop:10, padding:isMobile?"9px":"6px", border:"1px dashed var(--border-soft)", borderRadius:5, background:"transparent", cursor:"pointer", color:"var(--ink-faint)", fontSize:11 }}>+ Add group</button>
              )
            )}
          </div>
        );
      })}

      {deleteListConfirm && (
        <ConfirmDeleteListModal list={deleteListConfirm} onConfirm={() => deleteList(deleteListConfirm.id)} onCancel={() => setDeleteListConfirm(null)} />
      )}
      {deleteGroupConfirm && (
        <ConfirmDeleteGroupModal
          group={deleteGroupConfirm.group}
          itemCount={deleteGroupConfirm.itemCount}
          onKeepItems={() => deleteGroup(deleteGroupConfirm.listId, deleteGroupConfirm.group.id, false)}
          onDeleteItems={() => deleteGroup(deleteGroupConfirm.listId, deleteGroupConfirm.group.id, true)}
          onCancel={() => setDeleteGroupConfirm(null)}
        />
      )}
    </div>
  );
}

// ─── DAILY INTENTION ────────────────────────────────────────────────────────
// Morning ritual: pick 3-5 words for who you want to be today, get a real
// web-searched quote to match, review both all day, then a 1-5 evening
// scorecard per phrase. Deliberately NOT lens-scoped data (unlike tasks/
// goals/lists) — this is a single personal daily record, not something that
// makes sense split by work/home/gmu/growth — but `lens` is still accepted
// purely to tint the UI to whatever lens tab is active, same as GreetingStrip.
const INTENTION_WORKER_URL = "https://anthropic-proxy-worker.seancprince.workers.dev";

function todayIntentionRecord(intentions) {
  const key = toDateKey(new Date());
  return (intentions || []).find(d => d.date === key) || null;
}

function scoreTone(score) {
  if (score == null) return { base: "var(--ink-faint)", text: "var(--ink-faint)" };
  if (score >= 4) return { base: "var(--green)", text: "var(--green-text)" };
  if (score >= 2.5) return { base: "var(--amber)", text: "var(--amber-text)" };
  return { base: "var(--red)", text: "var(--red-text)" };
}

function dayAverage(day) {
  const scored = (day.phrases || []).filter(p => typeof p.score === "number");
  if (!scored.length) return null;
  return scored.reduce((s, p) => s + p.score, 0) / scored.length;
}

// A day only counts toward the streak once BOTH the morning phrases and the
// evening scorecard are done. Today itself is allowed to be incomplete
// without breaking a streak that ended yesterday — it just doesn't extend it
// yet — but any earlier gap (a skipped day) breaks the count.
function computeStreak(intentions) {
  const byDate = new Map((intentions || []).map(d => [d.date, d]));
  const isComplete = (d) => !!(d && d.morningCompletedAt && d.eveningCompletedAt);
  let streak = 0;
  const cursor = new Date();
  let first = true;
  for (;;) {
    const key = toDateKey(cursor);
    const day = byDate.get(key);
    if (isComplete(day)) streak++;
    else if (!first) break;
    first = false;
    cursor.setDate(cursor.getDate() - 1);
    if (streak > 3650) break; // sanity valve, never actually reached
  }
  return streak;
}

function avgLast30(intentions) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  const cutoffKey = toDateKey(cutoff);
  const scores = [];
  (intentions || []).forEach(d => {
    if (d.date < cutoffKey) return;
    (d.phrases || []).forEach(p => { if (typeof p.score === "number") scores.push(p.score); });
  });
  if (!scores.length) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

function makePhraseRow() {
  return { id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, text: "", note: "" };
}

function buildIntentionQuotePrompt(phrases, feedback, recentQuotes) {
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const phraseLines = phrases.map(p => `- ${p.text}${p.note ? ` — ${p.note}` : ""}`).join("\n");
  const liked = (feedback?.liked || []).slice(-8);
  const disliked = (feedback?.disliked || []).slice(-20);
  const likedBlock = liked.length
    ? `\n\nSean has previously liked quotes from/like:\n${liked.map(l => `- "${l.text}" — ${l.author}`).join("\n")}\nLean toward similar tone/authors when it genuinely fits today's theme, but don't force it.`
    : "";
  const dislikedBlock = disliked.length
    ? `\n\nAvoid these authors and quotes entirely — never return anything by them or substantially similar in style:\n${disliked.map(d => `- ${d.author}: "${d.text}"`).join("\n")}`
    : "";
  const skipBlock = (recentQuotes || []).length
    ? `\n\nDo not repeat any of these exact quotes already shown recently:\n${recentQuotes.slice(-20).map(t => `- "${t}"`).join("\n")}`
    : "";
  return `Today is ${today}.\n\nSean has chosen these words to guide who he wants to be today:\n${phraseLines}\n\nSearch the web for one real, attributed, well-known inspirational quote that authentically fits this theme — about seizing the day, becoming your best self, or being a great husband, father, and person. It must be a genuine quote from a real, identifiable person (never AI-generated, never anonymous or "unknown"), found via live search, with a verifiable source.${likedBlock}${dislikedBlock}${skipBlock}\n\nReturn exactly one quote as a JSON object. No markdown fences, no preamble, no explanation before or after:\n\n{\n  "text": "The exact quote text, no surrounding quotation marks",\n  "author": "Full name of the real person who said or wrote it",\n  "source": "Where it's from if known (book, speech, interview), else empty string",\n  "url": "A real, working URL from your search results supporting attribution"\n}`;
}

async function fetchIntentionQuote(phrases, feedback, recentQuotes) {
  const prompt = buildIntentionQuotePrompt(phrases, feedback, recentQuotes);
  const res = await fetch(INTENTION_WORKER_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1200, tools: [{ type: "web_search_20250305", name: "web_search" }], messages: [{ role: "user", content: prompt }] }) });
  const data = await res.json();
  const allText = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
  const parsed = extractJSON(allText.replace(/```json|```/g, "").trim());
  if (!parsed || !parsed.text || !parsed.author) return null;
  return { text: parsed.text, author: parsed.author, source: parsed.source || "", url: parsed.url || "" };
}

export function IntentionWidget({ intentions, lens, onOpenFull }) {
  const accent = lensAccent(lens);
  const accentText = lensAccentText(lens);
  const today = todayIntentionRecord(intentions);
  const streak = computeStreak(intentions);

  return (
    <div style={{ ...panel, minWidth: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ ...monoLabel, marginBottom: 0 }}>☀ Today's Intention</div>
        {streak > 0 && <span style={{ fontSize: 11, color: "var(--ink-soft)", fontFamily: "'DM Mono',monospace" }}>🔥 {streak}-day streak</span>}
      </div>

      {!today ? (
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <div style={{ fontSize: 12, color: "var(--ink-faint)", marginBottom: 12 }}>You haven't set an intention for today yet.</div>
          <button onClick={onOpenFull} style={{ padding: "7px 16px", background: accent, border: "none", borderRadius: 6, color: "var(--on-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Set Today's Intention</button>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: today.quote ? 8 : 0 }}>
              {today.phrases.map(p => (
                <span key={p.id} style={{ padding: "4px 10px", borderRadius: 999, background: tint(accent, 13), border: `1px solid ${tint(accent, 27)}`, fontSize: 12, fontWeight: 600, color: accentText }}>
                  {p.text}{typeof p.score === "number" && <span style={{ marginLeft: 5, opacity: 0.75 }}>★{p.score}</span>}
                </span>
              ))}
            </div>
            {today.quote && (
              <div style={{ fontSize: 12, color: "var(--ink-soft)", fontStyle: "italic", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                "{trunc(today.quote.text, 120)}" — {today.quote.author}
              </div>
            )}
          </div>
          <button onClick={onOpenFull} style={{ flexShrink: 0, padding: "7px 16px", background: "transparent", border: `1px solid ${tint(accent, 27)}`, borderRadius: 6, color: accentText, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
            {today.eveningCompletedAt ? "View Today's Recap" : "Open Intention"}
          </button>
        </div>
      )}
    </div>
  );
}

function MorningSetupForm({ accent, onSubmit }) {
  const [rows, setRows] = useState(() => [makePhraseRow(), makePhraseRow(), makePhraseRow()]);
  const canSubmit = rows.length >= 3 && rows.length <= 5 && rows.every(r => r.text.trim().length > 0);

  function updateRow(id, field, value) { setRows(rs => rs.map(r => r.id === id ? { ...r, [field]: value } : r)); }
  function removeRow(id) { setRows(rs => rs.length > 3 ? rs.filter(r => r.id !== id) : rs); }
  function addRow() { setRows(rs => rs.length < 5 ? [...rs, makePhraseRow()] : rs); }
  function submit() {
    if (!canSubmit) return;
    onSubmit(rows.map(r => ({ id: r.id, text: r.text.trim(), note: r.note.trim(), score: null, eveningNote: "" })));
  }

  let message = null;
  if (rows.length < 3) message = "Add at least 3 phrases.";
  else if (rows.some(r => !r.text.trim())) message = "Fill in every phrase, or remove the empty row.";

  return (
    <div style={panel}>
      <div style={{ ...monoLabel, marginBottom: 14 }}>This Morning — Who do you want to be today?</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
        {rows.map(row => (
          <div key={row.id} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
              <input value={row.text} maxLength={24} onChange={e => updateRow(row.id, "text", e.target.value)} placeholder="e.g. patient" style={{ background: "var(--bg)", border: "1px solid var(--border-soft)", borderRadius: 5, padding: "8px 10px", color: "var(--ink)", fontSize: 13, fontWeight: 600, outline: "none", fontFamily: "'DM Sans',sans-serif" }} />
              <input value={row.note} maxLength={120} onChange={e => updateRow(row.id, "note", e.target.value)} placeholder="Optional note — what this means today" style={{ background: "var(--bg)", border: "1px solid var(--border-soft)", borderRadius: 5, padding: "6px 10px", color: "var(--ink-soft)", fontSize: 11, outline: "none", fontFamily: "'DM Sans',sans-serif" }} />
            </div>
            <button onClick={() => removeRow(row.id)} disabled={rows.length <= 3} title="Remove" style={{ background: "transparent", border: "none", cursor: rows.length <= 3 ? "default" : "pointer", color: rows.length <= 3 ? "var(--ink-disabled)" : "var(--ink-faint)", fontSize: 16, padding: "6px 4px" }}>×</button>
          </div>
        ))}
      </div>
      <button onClick={addRow} disabled={rows.length >= 5} style={{ width: "100%", marginBottom: 14, padding: "7px", border: "1px dashed var(--border-soft)", borderRadius: 5, background: "transparent", cursor: rows.length >= 5 ? "default" : "pointer", color: rows.length >= 5 ? "var(--ink-disabled)" : "var(--ink-faint)", fontSize: 11 }}>
        {rows.length >= 5 ? "Maximum 5 phrases" : "+ Add another (max 5)"}
      </button>
      {message && <div style={{ fontSize: 11, color: "var(--red-text)", marginBottom: 10 }}>{message}</div>}
      <button onClick={submit} disabled={!canSubmit} style={{ width: "100%", padding: "10px", background: canSubmit ? accent : "var(--border-soft)", border: "none", borderRadius: 7, color: canSubmit ? "var(--on-accent)" : "var(--ink-faint)", cursor: canSubmit ? "pointer" : "default", fontSize: 13, fontWeight: 600 }}>
        Set Intention & Find a Quote
      </button>
    </div>
  );
}

function QuoteCard({ day, accent, quoteLoading, quoteError, isLiked, isDisliked, onLike, onDislike, onRegenerate }) {
  return (
    <div style={{ ...panel, borderLeft: `3px solid ${accent}` }}>
      <div style={{ ...monoLabel, marginBottom: 10 }}>Today's Quote</div>
      {quoteLoading ? (
        <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
          <span className="pulse-dot" style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: accent, marginRight: 8 }} />
          Finding today's quote…
        </div>
      ) : day.quote ? (
        <>
          <div style={{ fontSize: 16, fontStyle: "italic", color: "var(--ink)", lineHeight: 1.6, fontFamily: "'Playfair Display',serif", marginBottom: 8 }}>"{day.quote.text}"</div>
          <div style={{ fontSize: 12, color: "var(--ink-soft)", marginBottom: 14 }}>— {day.quote.author}{day.quote.source ? `, ${day.quote.source}` : ""}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={onLike} style={{ padding: "5px 12px", background: isLiked ? tint("var(--green)", 20) : "transparent", border: `1px solid ${isLiked ? "var(--green)" : "var(--border-soft)"}`, borderRadius: 6, color: isLiked ? "var(--green-text)" : "var(--ink-soft)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>👍 {isLiked ? "Saved" : "Like & Save"}</button>
            <button onClick={onDislike} style={{ padding: "5px 12px", background: isDisliked ? tint("var(--red)", 20) : "transparent", border: `1px solid ${isDisliked ? "var(--red)" : "var(--border-soft)"}`, borderRadius: 6, color: isDisliked ? "var(--red-text)" : "var(--ink-soft)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>👎 Not for me</button>
            <button onClick={onRegenerate} style={{ padding: "5px 12px", background: "transparent", border: "1px solid var(--border-soft)", borderRadius: 6, color: "var(--ink-soft)", cursor: "pointer", fontSize: 11 }}>↺ New quote</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: "var(--ink-faint)", marginBottom: 10 }}>{quoteError || "No quote yet."}</div>
          <button onClick={onRegenerate} style={{ padding: "6px 14px", background: accent, border: "none", borderRadius: 6, color: "var(--on-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Find a Quote</button>
        </>
      )}
    </div>
  );
}

function PhraseReviewCard({ phrase, accent, mode, onScoreChange, onNoteChange }) {
  const tone = scoreTone(phrase.score);
  return (
    <div style={{ ...panel, padding: "14px 16px" }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: "var(--ink)", fontFamily: "'Playfair Display',serif" }}>{phrase.text}</div>
      {phrase.note && <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4 }}>{phrase.note}</div>}

      {mode === "scoring" && (
        <>
          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n} onClick={() => onScoreChange(n)} style={{ width: 32, height: 32, borderRadius: "50%", border: `1px solid ${phrase.score === n ? accent : "var(--border-soft)"}`, background: phrase.score === n ? accent : "transparent", color: phrase.score === n ? "var(--on-accent)" : "var(--ink-soft)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{n}</button>
            ))}
          </div>
          <input value={phrase.eveningNote || ""} onChange={e => onNoteChange(e.target.value)} placeholder={phrase.score && phrase.score <= 3 ? "What happened? (especially useful on a low score)" : "Optional note"} style={{ width: "100%", marginTop: 10, background: "var(--bg)", border: "1px solid var(--border-soft)", borderRadius: 5, padding: "7px 10px", color: "var(--ink)", fontSize: 12, outline: "none", fontFamily: "'DM Sans',sans-serif" }} />
        </>
      )}

      {mode === "recap" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <span style={{ padding: "3px 10px", borderRadius: 999, background: tint(tone.base, 15), color: tone.text, fontSize: 12, fontWeight: 700 }}>★ {phrase.score ?? "—"}</span>
          {phrase.eveningNote && <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>{phrase.eveningNote}</span>}
        </div>
      )}
    </div>
  );
}

function IntentionTodayPanel({ intentions, quoteFeedback, lens, onIntentionsChange, onQuoteLibraryChange, onQuoteFeedbackChange, quoteLibrary }) {
  const accent = lensAccent(lens);
  const todayKey = toDateKey(new Date());
  const today = intentions.find(d => d.date === todayKey) || null;

  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [scoringOpen, setScoringOpen] = useState(false);
  const [eveningDraft, setEveningDraft] = useState(null);
  const sessionShown = useRef([]);

  async function curateQuote(dayForPrompt, baseList) {
    setQuoteLoading(true); setQuoteError(null);
    const recentQuotes = [...baseList.slice(-20).map(d => d.quote?.text).filter(Boolean), ...sessionShown.current];
    try {
      const q = await fetchIntentionQuote(dayForPrompt.phrases, quoteFeedback, recentQuotes);
      if (!q) { setQuoteError("Couldn't find a quote — try again."); return; }
      sessionShown.current = [...sessionShown.current, q.text];
      const finalDay = { ...dayForPrompt, quote: q };
      onIntentionsChange([...baseList.filter(d => d.date !== dayForPrompt.date), finalDay]);
    } catch {
      setQuoteError("Couldn't find a quote — try again.");
    } finally {
      setQuoteLoading(false);
    }
  }

  function handleMorningSubmit(phrases) {
    const newDay = { date: todayKey, phrases, quote: null, morningCompletedAt: new Date().toISOString(), eveningCompletedAt: null };
    const updatedList = [...intentions.filter(d => d.date !== todayKey), newDay];
    onIntentionsChange(updatedList);
    curateQuote(newDay, updatedList);
  }

  function handleRegenerate() {
    if (!today || quoteLoading) return;
    curateQuote(today, intentions);
  }

  function handleLike() {
    if (!today?.quote) return;
    const q = today.quote;
    if (!quoteLibrary.some(s => s.text === q.text && s.author === q.author)) {
      onQuoteLibraryChange([...quoteLibrary, { id: `q-${Date.now()}`, text: q.text, author: q.author, source: q.source, url: q.url, savedAt: new Date().toISOString(), forDate: today.date }]);
    }
    if (!quoteFeedback.liked.some(l => l.text === q.text && l.author === q.author)) {
      onQuoteFeedbackChange({ ...quoteFeedback, liked: [...quoteFeedback.liked, { author: q.author, text: q.text, ts: Date.now() }].slice(-60) });
    }
  }

  function handleDislike() {
    if (!today?.quote) return;
    const q = today.quote;
    onQuoteFeedbackChange({ ...quoteFeedback, disliked: [...quoteFeedback.disliked, { author: q.author, text: q.text, ts: Date.now() }].slice(-60) });
    curateQuote(today, intentions);
  }

  function openEveningForm() { setEveningDraft(today.phrases.map(p => ({ ...p }))); setScoringOpen(true); }
  function updateDraftScore(id, score) { setEveningDraft(d => d.map(p => p.id === id ? { ...p, score } : p)); }
  function updateDraftNote(id, note) { setEveningDraft(d => d.map(p => p.id === id ? { ...p, eveningNote: note } : p)); }
  function saveEvening() {
    if (!eveningDraft.every(p => typeof p.score === "number")) return;
    const finalDay = { ...today, phrases: eveningDraft, eveningCompletedAt: today.eveningCompletedAt || new Date().toISOString() };
    onIntentionsChange([...intentions.filter(d => d.date !== todayKey), finalDay]);
    setScoringOpen(false); setEveningDraft(null);
  }
  function cancelEvening() { setScoringOpen(false); setEveningDraft(null); }

  const isLiked = today?.quote ? quoteFeedback.liked.some(l => l.text === today.quote.text && l.author === today.quote.author) : false;
  const isDisliked = today?.quote ? quoteFeedback.disliked.some(l => l.text === today.quote.text && l.author === today.quote.author) : false;
  const canScoreYet = new Date().getHours() >= 17;

  if (!today) return <MorningSetupForm accent={accent} onSubmit={handleMorningSubmit} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(scoringOpen ? eveningDraft : today.phrases).map(p => (
          <PhraseReviewCard
            key={p.id}
            phrase={p}
            accent={accent}
            mode={scoringOpen ? "scoring" : (today.eveningCompletedAt ? "recap" : "display")}
            onScoreChange={n => updateDraftScore(p.id, n)}
            onNoteChange={v => updateDraftNote(p.id, v)}
          />
        ))}
      </div>

      <QuoteCard day={today} accent={accent} quoteLoading={quoteLoading} quoteError={quoteError} isLiked={isLiked} isDisliked={isDisliked} onLike={handleLike} onDislike={handleDislike} onRegenerate={handleRegenerate} />

      {scoringOpen ? (
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={saveEvening} disabled={!eveningDraft.every(p => typeof p.score === "number")} style={{ flex: 1, padding: "10px", background: eveningDraft.every(p => typeof p.score === "number") ? accent : "var(--border-soft)", border: "none", borderRadius: 7, color: eveningDraft.every(p => typeof p.score === "number") ? "var(--on-accent)" : "var(--ink-faint)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Save Evening Review</button>
          <button onClick={cancelEvening} style={{ padding: "10px 16px", background: "transparent", border: "1px solid var(--border-soft)", borderRadius: 7, color: "var(--ink-soft)", cursor: "pointer", fontSize: 13 }}>Cancel</button>
        </div>
      ) : today.eveningCompletedAt ? (
        <button onClick={openEveningForm} style={{ alignSelf: "flex-start", background: "transparent", border: "none", color: lensAccentText(lens), cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>Edit tonight's scores</button>
      ) : canScoreYet ? (
        <button onClick={openEveningForm} style={{ padding: "10px", background: accent, border: "none", borderRadius: 7, color: "var(--on-accent)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Evening Scorecard</button>
      ) : (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "var(--ink-faint)", marginBottom: 6 }}>Evening scoring opens at 5pm.</div>
          <button onClick={openEveningForm} style={{ background: "transparent", border: "none", color: "var(--ink-faint)", cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>Score early anyway</button>
        </div>
      )}
    </div>
  );
}

function IntentionHistoryPanel({ intentions, lens }) {
  const isMobile = useIsMobile();
  const accent = lensAccent(lens);
  const [expanded, setExpanded] = useState(null);
  const streak = computeStreak(intentions);
  const avg30 = avgLast30(intentions);
  const totalDays = intentions.filter(d => d.morningCompletedAt).length;
  const todayKey = toDateKey(new Date());
  const pastDays = intentions.filter(d => d.date !== todayKey).sort((a, b) => b.date.localeCompare(a.date));

  const tiles = [
    { label: "Streak", value: streak ? `${streak}🔥` : "0", sub: "consecutive full days" },
    { label: "30-Day Avg", value: avg30 != null ? avg30.toFixed(1) : "—", sub: "across all phrases" },
    { label: "Days Tracked", value: String(totalDays), sub: "total" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${isMobile ? 1 : 3},1fr)`, gap: 10 }}>
        {tiles.map(t => (
          <div key={t.label} style={{ ...panel, borderLeft: `3px solid ${accent}`, padding: "12px 14px" }}>
            <div style={{ ...monoLabel, marginBottom: 6 }}>{t.label}</div>
            <div style={{ fontSize: 26, fontWeight: 600, color: "var(--ink)", fontFamily: "'Playfair Display',serif", lineHeight: 1 }}>{t.value}</div>
            <div style={{ fontSize: 11, color: "var(--ink-soft)", marginTop: 4 }}>{t.sub}</div>
          </div>
        ))}
      </div>

      {pastDays.length === 0 ? (
        <div style={panel}><div style={{ fontSize: 13, color: "var(--ink-faint)", padding: "20px 0", textAlign: "center" }}>No past days yet — come back tomorrow.</div></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {pastDays.map(day => {
            const avg = dayAverage(day);
            const tone = scoreTone(avg);
            const isOpen = expanded === day.date;
            return (
              <div key={day.date} style={panel}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 10, flexWrap: "wrap" }} onClick={() => setExpanded(isOpen ? null : day.date)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", fontFamily: "'DM Mono',monospace" }}>{new Date(day.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                    {day.phrases.map(p => (
                      <span key={p.id} style={{ fontSize: 11, color: "var(--ink-soft)" }}>{p.text}{typeof p.score === "number" && ` ★${p.score}`}</span>
                    ))}
                  </div>
                  <span style={{ padding: "3px 10px", borderRadius: 999, background: tint(tone.base, 15), color: tone.text, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{avg != null ? avg.toFixed(1) : "—"}</span>
                </div>
                {isOpen && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--panel-alt)", display: "flex", flexDirection: "column", gap: 8 }}>
                    {day.phrases.map(p => (
                      <div key={p.id} style={{ fontSize: 12, color: "var(--ink-mid)" }}>
                        <strong style={{ color: "var(--ink)" }}>{p.text}</strong>{p.note && <span style={{ color: "var(--ink-faint)" }}> — {p.note}</span>}
                        {p.eveningNote && <div style={{ marginTop: 2, color: "var(--ink-soft)" }}>{p.eveningNote}</div>}
                      </div>
                    ))}
                    {day.quote && <div style={{ fontSize: 11, color: "var(--ink-faint)", fontStyle: "italic", marginTop: 4 }}>"{day.quote.text}" — {day.quote.author}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SavedQuotesModal({ quoteLibrary, onClose, onRemove }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--panel-soft)", border: "1px solid var(--border)", borderRadius: 10, padding: 20, maxWidth: 480, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Saved Quotes</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-faint)", fontSize: 16 }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
          {quoteLibrary.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--ink-faint)", padding: "20px 0", textAlign: "center" }}>No saved quotes yet — like one from your daily intention to keep it here.</div>
          ) : [...quoteLibrary].reverse().map(q => (
            <div key={q.id} style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: 13, fontStyle: "italic", color: "var(--ink)", lineHeight: 1.5 }}>"{q.text}"</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, gap: 8 }}>
                <span style={{ fontSize: 11, color: "var(--ink-soft)" }}>— {q.author}{q.source ? `, ${q.source}` : ""}</span>
                <button onClick={() => onRemove(q)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-faint)", fontSize: 11, flexShrink: 0 }}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function IntentionView({ intentions, quoteLibrary, quoteFeedback, lens, onIntentionsChange, onQuoteLibraryChange, onQuoteFeedbackChange }) {
  const accent = lensAccent(lens);
  const accentText = lensAccentText(lens);
  const [subview, setSubview] = useState("today");
  const [showSaved, setShowSaved] = useState(false);

  function removeSavedQuote(q) {
    onQuoteLibraryChange(quoteLibrary.filter(s => s.id !== q.id));
    onQuoteFeedbackChange({ ...quoteFeedback, liked: quoteFeedback.liked.filter(l => !(l.text === q.text && l.author === q.author)) });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[{ id: "today", label: "Today" }, { id: "history", label: "History" }].map(t => (
            <button key={t.id} onClick={() => setSubview(t.id)} style={{ padding: "6px 16px", borderRadius: 999, border: `1px solid ${subview === t.id ? accent : "var(--border-soft)"}`, background: subview === t.id ? tint(accent, 13) : "transparent", color: subview === t.id ? accentText : "var(--ink-soft)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.label}</button>
          ))}
        </div>
        <button onClick={() => setShowSaved(true)} style={{ padding: "6px 14px", background: "transparent", border: "1px solid var(--border-soft)", borderRadius: 6, color: "var(--ink-soft)", cursor: "pointer", fontSize: 12 }}>🔖 Saved Quotes{quoteLibrary.length > 0 ? ` (${quoteLibrary.length})` : ""}</button>
      </div>

      {subview === "today"
        ? <IntentionTodayPanel intentions={intentions} quoteLibrary={quoteLibrary} quoteFeedback={quoteFeedback} lens={lens} onIntentionsChange={onIntentionsChange} onQuoteLibraryChange={onQuoteLibraryChange} onQuoteFeedbackChange={onQuoteFeedbackChange} />
        : <IntentionHistoryPanel intentions={intentions} lens={lens} />
      }

      {showSaved && <SavedQuotesModal quoteLibrary={quoteLibrary} onClose={() => setShowSaved(false)} onRemove={removeSavedQuote} />}
    </div>
  );
}
